import hexToRgba from "hex-to-rgba";
import moment from "moment";
import {config} from "@/config/config";
import {isOffline} from "./offline-utils";
import axios from "axios";

/**
 * Retrieves the session identifier (SSID) based on the current environment and stores it in the local storage if it does not already exist.
 * If running in an embedded environment or iframe, it uses `"ssid-embed"` as the key. Otherwise, it uses `"ssid"`.
 *
 * @return {string} The session identifier (SSID) from local storage.
 */
export function getSSID() {
  let urlParams = typeof window !== "undefined" ? new URL(document.location).searchParams : new URLSearchParams();
  let ssid;
  if (typeof window !== "undefined")
    if (window.self !== window.top || /^\/embed/.test(location.pathname)) {
      if (
        !localStorage.getItem("ssid-embed") ||
        (urlParams.get("api_key") &&
          localStorage.getItem("ssid-embed") &&
          localStorage.getItem("ssid-embed") !== urlParams.get("api_key"))
      ) {
        localStorage.setItem("ssid-embed", urlParams.get("api_key") ? ('embedded-' + urlParams.get("api_key")) : ('embedded-' + Date.now()));
      }
      ssid = localStorage.getItem("ssid-embed");
    } else {
      if (!localStorage.getItem("ssid")) {
        localStorage.setItem("ssid", 'main');
      }
      ssid = localStorage.getItem("ssid");
    }
  return ssid;
}

export function formatDate(date, locale, format) {
  if (!format) {
    format =
      moment(date).format("YYYY") === moment().format("YYYY")
        ? "ddd, DD MMM @ HH:mm"
        : "ddd, DD MMM, YYYY @ HH:mm";
  }
  return locale ? moment(date).locale(locale).format(format) : moment(date).format(format);
}

export function deepSearch(object, searchKey) {
  function findValues(obj, key) {
    return findValuesHelper(obj, key, []);
  }

  function findValuesHelper(obj, key, list) {
    let i;
    if (!obj) {
      return list;
    }
    if (obj instanceof Array) {
      for (i in obj) {
        list = list.concat(findValuesHelper(obj[i], key, []));
      }
      return list;
    }
    if (obj[key]) {
      list.push(obj[key]);
    }

    if (typeof obj === "object") {
      const children = Object.keys(obj);
      if (children.length > 0) {
        for (i = 0; i < children.length; i++) {
          list = list.concat(findValuesHelper(obj[children[i]], key, []));
        }
      }
    }
    return list;
  }

  return findValues(object, searchKey);
}

export function replaceFlags(string, values) {
  if (values) {
    for (let i = 0; i < values.length; i++) {
      string = string.replace("%s", values[i]);
    }
  }
  return string;
}

export function getLayerTypeForShapeType(name) {
  if (/polygon/gi.test(name)) {
    return "fill";
  } else if (/line/gi.test(name)) {
    return "line";
  } else {
    return "circle";
  }
}

export function kebabize(str) {
  return str
    .split("")
    .map((letter, idx) => {
      return letter.toUpperCase() === letter ? `${idx !== 0 ? "-" : ""}${letter.toLowerCase()}` : letter;
    })
    .join("");
}

export const mapboxStyles = {
  fill: {
    visibility: {
      title: "Visibility",
      dataType: "list",
      value: "visible",
      options: ["visible", "none"],
      type: "layout",
      expressions: false,
      featureState: false
    },
    "fill-antialias": {
      title: "Fill antialias",
      dataType: "boolean",
      value: true,
      type: "paint",
      expressions: false,
      featureState: false
    },
    "fill-color": {
      title: "Fill color",
      dataType: "color",
      value: "#000000",
      type: "paint",
      expressions: true,
      featureState: true
    },
    "fill-opacity": {
      title: "Fill opacity",
      dataType: "range",
      value: 1,
      options: [0, 1],
      type: "paint",
      expressions: true,
      featureState: true
    },
    "fill-pattern": {
      title: "Fill pattern",
      dataType: "file",
      value: "",
      type: "paint",
      expressions: true,
      featureState: true
    }
    //'fill-translate': {title: "Fill translate", dataType: "array", value: [], options: [0, 0], type: "paint", expressions: true},
    /*'fill-translate-anchor': {
      title: "Fill translate anchor",
      dataType: "list",
      value: "map",
      options: ["map", "viewport"],
      type: "paint",
      expressions: false
    },*/
    //'fill-sort-key': { title: "Fill sort key", dataType: "number", value: 0, type: "layout", expressions: false },
  },
  border: {
    "line-color": {
      title: "Line color",
      dataType: "color",
      value: "#000000",
      type: "paint",
      expressions: true,
      featureState: true
    },
    "line-width": {
      title: "Line width",
      dataType: "number",
      value: 2,
      type: "paint",
      expressions: true,
      featureState: true
    },
    "line-opacity": {
      title: "Line opacity",
      dataType: "range",
      value: 1,
      options: [0, 1],
      type: "paint",
      expressions: true,
      featureState: true
    },
    "line-blur": {
      title: "Line blur",
      dataType: "range",
      value: 0,
      options: [0, 1],
      type: "paint",
      expressions: true,
      featureState: true
    },
    "line-pattern": {
      title: "Line pattern",
      dataType: "file",
      value: "",
      type: "paint",
      expressions: true,
      featureState: false
    },
  },
  circle: {
    visibility: {
      title: "Visibility",
      dataType: "list",
      value: "visible",
      options: ["visible", "none"],
      type: "layout",
      expressions: false,
      featureState: false
    },
    "circle-blur": {
      title: "Circle blur",
      dataType: "range",
      value: 0,
      options: [0, 1],
      type: "paint",
      expressions: true,
      featureState: true
    },
    "circle-color": {
      title: "Circle color",
      dataType: "color",
      value: "#000000",
      type: "paint",
      expressions: true,
      featureState: true
    },
    "circle-opacity": {
      title: "Circle opacity",
      dataType: "range",
      value: 1,
      options: [0, 1],
      type: "paint",
      expressions: true,
      featureState: true
    },
    /*'circle-pitch-alignment': {
      title: "Circle pitch alignment",
      dataType: "list",
      value: "map",
      options: ["map", "viewport"],
      type: "paint",
      expressions: false
    },
    'circle-pitch-scale': {
      title: "Circle pitch scale",
      dataType: "list",
      value: "map",
      options: ["map", "viewport"],
      type: "paint",
      expressions: false
    },*/
    "circle-radius": {
      title: "Circle radius",
      dataType: "number",
      value: 5,
      type: "paint",
      expressions: true,
      featureState: true
    },
    "circle-stroke-color": {
      title: "Circle stroke color",
      dataType: "color",
      value: "#000000",
      type: "paint",
      expressions: true,
      featureState: true
    },
    "circle-stroke-opacity": {
      title: "Circle stroke opacity",
      dataType: "range",
      value: 1,
      options: [0, 1],
      type: "paint",
      expressions: true,
      featureState: true
    },
    "circle-stroke-width": {
      title: "Circle stroke width",
      dataType: "number",
      value: 1,
      type: "paint",
      expressions: true,
      featureState: true
    },
    /*'circle-translate': {title: "Circle translate", dataType: "array", value: [], options: [0, 0], type: "paint", expressions: false},
    'circle-translate-anchor': {
      title: "Circle translate anchor",
      dataType: "list",
      value: "map",
      options: ["map", "viewport"],
      type: "paint",
      expressions: false
    },*/
    //'circle-sort-key': {title: "Circle sort key", dataType: "number", value: 0, type: "layout", expressions: false},
    "icon-image": {
      title: "Icon URL",
      dataType: "file",
      value: "",
      type: "layout",
      expressions: true,
      featureState: false
    },
    "icon-size": {
      title: "Icon size",
      dataType: "number",
      value: 1,
      type: "layout",
      expressions: true,
      featureState: true
    },
    "icon-color": {
      title: "Icon color",
      dataType: "color",
      value: "#000000",
      type: "paint",
      expressions: true,
      featureState: true
    },
    "icon-allow-overlap": {
      title: "Icon allow overlap",
      dataType: "boolean",
      value: true,
      type: "layout",
      expressions: false,
      featureState: false
    },
    "icon-ignore-placement": {
      title: "Icon ignore placement",
      dataType: "boolean",
      value: false,
      type: "layout",
      expressions: false,
      featureState: false
    },
  },
  line: {
    visibility: {
      title: "Visibility",
      dataType: "list",
      value: "visible",
      options: ["visible", "none"],
      type: "layout",
      expressions: false,
      featureState: false
    },
    "line-blur": {
      title: "Line blur",
      dataType: "range",
      value: 0,
      options: [0, 1],
      type: "paint",
      expressions: true,
      featureState: true
    },
    "line-color": {
      title: "Line color",
      dataType: "color",
      value: "#000000",
      type: "paint",
      expressions: true,
      featureState: true
    },
    'line-dasharray': {
      title: "Line dasharray",
      dataType: "array",
      value: [],
      options: [0, 0],
      type: "paint",
      expressions: true,
      featureState: false
    },
    "line-gap-width": {
      title: "Line gap width",
      dataType: "number",
      value: 0,
      type: "paint",
      expressions: true,
      featureState: false
    },
    "line-gradient": {
      title: "Line gradient",
      dataType: "string",
      value: "",
      type: "paint",
      expressions: true,
      featureState: false
    },
    "line-offset": {
      title: "Line offset",
      dataType: "number",
      value: 0,
      type: "paint",
      expressions: true,
      featureState: false
    },
    "line-opacity": {
      title: "Line opacity",
      dataType: "range",
      value: 1,
      options: [0, 1],
      type: "paint",
      expressions: true,
      featureState: true
    },
    //'line-pattern': {title: "Line pattern", dataType: "number", value: 0, type: "paint", expressions: false},
    "line-pattern": {
      title: "Line pattern",
      dataType: "file",
      value: "",
      type: "paint",
      expressions: true,
      featureState: false
    },
    //'line-translate': {title: "Line translate", dataType: "array", value: [], options: [0, 0], type: "paint", expressions: true},
    /*'line-translate-anchor': {
      title: "Line translate anchor",
      dataType: "list",
      value: "map",
      options: ["map", "viewPort"],
      type: "paint",
      expressions: false
    },*/
    "line-width": {
      title: "Line width",
      dataType: "number",
      value: 1,
      type: "paint",
      expressions: true,
      featureState: true
    },
    "line-cap": {
      title: "Line cap",
      dataType: "list",
      value: "butt",
      options: ["butt", "round", "square"],
      type: "layout",
      expressions: false,
      featureState: false
    },
    "line-join": {
      title: "Line join",
      dataType: "list",
      value: "bevel",
      options: ["bevel", "round", "miter"],
      type: "layout",
      expressions: false,
      featureState: false
    },
    //'line-miter-limit': {title: "Line miter limit", dataType: "number", value: 0, type: "layout", expressions: true},
    //'line-round-limit': {title: "Line round limit", dataType: "number", value: 0, type: "layout", expressions: true},
    //'line-sort-key': {title: "Line sort key", dataType: "number", value: 0, type: "layout", expressions: false},
  },
  label: {
    "text-field": {
      title: "Text field",
      dataType: "list",
      value: "",
      options: [],
      type: "layout",
      expressions: false,
      featureState: false,
      group: "Text settings"
    },
    visibility: {
      title: "Visibility",
      dataType: "list",
      value: "visible",
      options: ["visible", "none"],
      type: "layout",
      expressions: false,
      featureState: false,
      group: "General settings"
    },
    "icon-color": {
      title: "Icon color",
      dataType: "color",
      value: "#000000",
      type: "paint",
      expressions: true,
      featureState: false,
      group: "Icon settings"
    },
    //'icon-halo-blur': { title: "Icon halo blur", dataType: "number", value: 0, type: "paint" },
    //'icon-halo-color': { title: "Icon halo color", dataType: "color", value: "#000000", type: "paint" },
    //'icon-halo-width': { title: "Icon halo width", dataType: "number", value: 0, type: "paint" },
    //'icon-opacity': { title: "Icon opacity", dataType: "range", value: 1, options: [0, 1], type: "paint" },
    //'icon-translate': { title: "Icon translate", dataType: "array", value: [], options: [0, 0], type: "paint" },
    //'icon-translate-anchor': { title: "Icon translate anchor", dataType: "list", value: "map", options: ["map", "viewport"], type: "paint" },
    "text-color": {
      title: "Text color",
      dataType: "color",
      value: "#000000",
      type: "paint",
      expressions: true,
      featureState: false,
      group: "Text settings"
    },
    "text-halo-blur": {
      title: "Text halo blur",
      dataType: "number",
      value: 0,
      type: "paint",
      expressions: true,
      featureState: false,
      group: "Text settings"
    },
    "text-halo-color": {
      title: "Text halo color",
      dataType: "color",
      value: "#000000",
      type: "paint",
      expressions: true,
      featureState: false,
      group: "Text settings"
    },
    "text-halo-width": {
      title: "Text halo width",
      dataType: "number",
      value: 0,
      type: "paint",
      expressions: true,
      featureState: false,
      group: "Text settings"
    },
    "text-opacity": {
      title: "Text opacity",
      dataType: "range",
      value: 1,
      options: [0, 1],
      type: "paint",
      expressions: true,
      featureState: false,
      group: "Text settings"
    },
    //'text-translate': { title: "Text translate", dataType: "array", value: [], options: [0, 0], type: "paint" },
    //'text-translate-anchor': { title: "Text translate anchor", dataType: "list", value: "map", options: ["map", "viewport"], type: "paint" },
    "icon-anchor": {
      title: "Icon anchor",
      dataType: "list",
      value: "center",
      options: ["center", "left", "top", "bottom", "top-left", "top-right", "bottom-left", "bottom-right"],
      type: "layout",
      expressions: true,
      featureState: false,
      group: "Icon settings"
    },
    "icon-ignore-placement": {
      title: "Icon ignore placement",
      dataType: "boolean",
      value: false,
      type: "layout",
      expressions: false,
      featureState: false,
      group: "Icon settings"
    },
    "icon-image": {
      title: "Icon image",
      dataType: "file",
      value: null,
      type: "layout",
      expressions: true,
      featureState: false,
      group: "Icon settings"
    },
    "icon-keep-upright": {
      title: "Icon keep uprights",
      dataType: "boolean",
      value: true,
      type: "layout",
      expressions: false,
      featureState: false,
      group: "Icon settings"
    },
    //'icon-offset': { title: "Icon offset", dataType: "array", value: [], options: [0, 0], type: "layout", group: 'Icon settings' },
    'icon-optional': {
      title: "Icon optional",
      dataType: "boolean",
      value: true,
      type: "layout",
      expressions: false,
      featureState: false,
      group: 'Icon settings'
    },
    "icon-allow-overlap": {
      title: "Icon allow overlap",
      dataType: "boolean",
      value: true,
      type: "layout",
      expressions: false,
      featureState: false,
      group: "Icon settings"
    },
    'icon-padding': {
      title: "Icon padding",
      dataType: "number",
      value: 0,
      type: "layout",
      expressions: false,
      featureState: false,
      group: 'Icon settings'
    },
    'icon-pitch-alignment': {
      title: "Icon pitch alignment",
      dataType: "list",
      value: "map",
      options: ["map", "viewport", "auto"],
      type: "layout",
      expressions: false,
      featureState: false,
      group: 'Icon settings'
    },
    'icon-rotate': {
      title: "Icon rotate",
      dataType: "number",
      value: 0,
      type: "layout",
      expressions: true,
      featureState: false,
      group: 'Icon settings'
    },
    'icon-rotation-alignment': {
      title: "Icon rotation alignment",
      dataType: "list",
      value: "map",
      options: ["map", "viewport", "auto"],
      type: "layout",
      expressions: false,
      featureState: false,
      group: 'Icon settings'
    },
    'icon-size': {
      title: "Icon size",
      dataType: "number",
      value: 0,
      type: "layout",
      expressions: true,
      featureState: false,
      group: 'Icon settings'
    },
    'icon-text-fit': {
      title: "Icon text fit",
      dataType: "list",
      value: "none",
      options: ["none", "width", "height", "both"],
      type: "layout",
      expressions: false,
      featureState: false,
      group: 'Icon settings'
    },
    //'icon-text-fit-padding': { title: "Icon text fit padding", dataType: "array", value: [], options: [0, 0], type: "layout", group: 'Icon settings' },
    "symbol-avoid-edges": {
      title: "Icon avoid edges",
      dataType: "boolean",
      value: true,
      type: "layout",
      expressions: false,
      featureState: false,
      group: "Icon settings"
    },
    "symbol-placement": {
      title: "Symbol placement",
      dataType: "list",
      value: "point",
      options: ["point", "line", "line-center"],
      type: "layout",
      expressions: false,
      featureState: false,
      group: "General settings"
    },
    "symbol-sort-key": {
      title: "Symbol sort key",
      dataType: "number",
      value: 0,
      type: "layout",
      expressions: false,
      featureState: false,
      group: "General settings"
    },
    'symbol-spacing': {
      title: "Symbol spacing",
      dataType: "number",
      value: 0,
      type: "layout",
      expressions: false,
      featureState: false,
      group: "General settings"
    },
    'symbol-z-order': {
      title: "Symbol z order",
      dataType: "list",
      value: "auto",
      options: ["auto", "viewport-y", "source"],
      type: "layout",
      expressions: false,
      featureState: false,
      group: "General settings"
    },
    "text-allow-overlap": {
      title: "Text allow overlap",
      dataType: "boolean",
      value: false,
      type: "layout",
      expressions: false,
      featureState: false,
      group: "Text settings"
    },
    "text-anchor": {
      title: "Text anchor",
      dataType: "list",
      value: "center",
      options: [
        "center",
        "left",
        "top",
        "bottom",
        "top-left",
        "top-right",
        "bottom-left",
        "bottom-right",
      ],
      type: "layout",
      expressions: false,
      featureState: false,
      group: "Text settings"
    },
    "text-font": {
      title: "Text font",
      dataType: "array",
      value: [],
      options: [],
      type: "layout",
      expressions: false,
      featureState: false,
      group: "Text settings",
      multi: true
    },
    'text-ignore-placement': {
      title: "Text ignore placement",
      dataType: "boolean",
      value: false,
      type: "layout",
      expressions: false,
      featureState: false,
      group: "Text settings"
    },
    "text-justify": {
      title: "Text justify",
      dataType: "list",
      value: "auto",
      options: ["auto", "left", "center", "right"],
      type: "layout",
      expressions: false,
      featureState: false,
      group: "Text settings"
    },
    //'text-keep-upright': { title: "Text keep upright", dataType: "boolean", value: false, type: "layout" },
    //'text-letter-spacing': { title: "Text letter spacing", dataType: "number", value: 0, type: "layout" },
    //'text-line-height': { title: "Text line height", dataType: "number", value: 0, type: "layout" },
    //'text-max-angle': { title: "Text max angle", dataType: "number", value: 0, type: "layout" },
    //'text-max-width': { title: "Text max width", dataType: "number", value: 0, type: "layout" },
    //'text-offset': { title: "Text offset", dataType: "array", value: [], options: [0, 0], type: "layout" },
    //'text-optional': { title: "Text optional", dataType: "boolean", value: false, type: "layout" },
    //'text-padding': { title: "Text padding", dataType: "number", value: 0, type: "layout" },
    //'text-pitch-alignment': { title: "Text pitch alignment", dataType: "list", value: "auto", options: ["map", "viewport", "auto"], type: "layout" },
    "text-radial-offset": {
      title: "Text radial offset",
      dataType: "number",
      value: 0,
      type: "layout",
      expressions: true,
      featureState: false,
      group: "Text settings"
    },
    'text-rotate': {
      title: "Text rotate", dataType: "number", value: null, type: "layout",
      expressions: true,
      featureState: false,
      group: "Text settings"
    },
    'text-rotation-alignment': {
      title: "Text rotation alignment", dataType: "list", value: "auto", options: ["map", "viewport", "auto"], type: "layout",
      expressions: true,
      featureState: false,
      group: "Text settings"
    },
    "text-size": {
      title: "Text size",
      dataType: "number",
      value: 10,
      type: "layout",
      expressions: true,
      featureState: false,
      group: "Text settings"
    },
    "text-transform": {
      title: "Text transform",
      dataType: "list",
      value: "none",
      options: ["none", "uppercase", "lowercase"],
      type: "layout",
      expressions: false,
      featureState: false,
      group: "Text settings"
    },
    'text-variable-anchor': {
      title: "Text variable anchor",
      dataType: "array",
      value: ["center"],
      options: ["center", "left", "top", "bottom", "top-left", "top-right", "bottom-left", "bottom-right"],
      type: "layout",
      expressions: true,
      featureState: false,
      group: "Text settings"
    },
    //'text-writing-mode': { title: "Text writing mode", dataType: "list", value: "horizontal", options: ["horizontal", "vertical"], type: "layout" },
    //"text-size-min-zoom": { title: "Min zoom", dataType: "number", value: 0, type: "layout" },
    //"text-size-max-zoom": { title: "Max zoom", dataType: "number", value: 22, type: "layout" },
  },
  raster: {
    "raster-opacity": {
      title: "Opacity",
      dataType: "range",
      value: 1,
      options: [0, 1],
      type: "paint",
      expressions: true,
      featureState: true,
    },
    "raster-brightness-max": {
      title: "Brightness max",
      dataType: "range",
      value: 1,
      options: [0, 1],
      type: "paint",
      expressions: true,
      featureState: true,
    },
    "raster-brightness-min": {
      title: "Brightness min",
      dataType: "range",
      value: 0,
      options: [0, 1],
      type: "paint",
      expressions: true,
      featureState: true,
    },
    "raster-contrast": {
      title: "Contrast",
      dataType: "range",
      value: 0,
      options: [-1, 1],
      type: "paint",
      expressions: true,
      featureState: true,
    },
    "raster-fade-duration": {
      title: "Fade duration",
      dataType: "number",
      value: 300,
      type: "paint",
      expressions: true,
      featureState: false,
    },
    "raster-hue-rotate": {
      title: "Hue rotate",
      dataType: "number",
      value: 0,
      type: "paint",
      expressions: true,
      featureState: false,
    },
    "raster-resampling": {
      title: "Resampling",
      dataType: "list",
      value: "linear",
      options: ["linear", "nearest"],
      type: "paint",
      expressions: false,
      featureState: false,
    },
    "raster-saturation": {
      title: "Saturation",
      dataType: "range",
      value: 0,
      options: [-1, 1],
      type: "paint",
      expressions: true,
      featureState: false,
    },
  },
  extrusion: {
    "fill-extrusion-color": {
      title: "Fill extrusion color",
      dataType: "color",
      value: "#000000",
      type: "paint",
      expressions: true,
      featureState: true,
    },
    "fill-extrusion-vertical-gradient": {
      title: "Fill extrusion vertical gradient",
      dataType: "boolean",
      value: true,
      type: "paint",
      expressions: false,
      featureState: false,
    },
    "fill-extrusion-height": {
      title: "Fill extrusion height",
      dataType: "list",
      value: 0,
      options: [],
      type: "paint",
      expressions: true,
      featureState: true,
    },
    "fill-extrusion-base": {
      title: "Fill extrusion base",
      dataType: "list",
      value: 0,
      options: [],
      type: "paint",
      expressions: true,
      featureState: true,
    },
    "fill-extrusion-opacity": {
      title: "Fill extrusion opacity",
      dataType: "range",
      value: 1,
      options: [0, 1],
      type: "paint",
      expressions: false,
      featureState: true,
    },
  },
  heatmap: {
    visibility: {
      title: "Visibility",
      dataType: "list",
      value: "visible",
      options: ["visible", "none"],
      type: "layout",
      expressions: false,
      featureState: false
    },
    "heatmap-color": {
      title: "Heatmap color",
      shortTitle: "Color",
      dataType: "color",
      value: "#000000",
      type: "paint",
      expressions: true,
      featureState: true,
      allowPlainValue: false,
      dataField: "heatmap-density"
    },
    "heatmap-intensity": {
      title: "Heatmap intensity",
      shortTitle: "Intensity",
      dataType: "range",
      value: 1,
      options: [0, 1],
      type: "paint",
      expressions: true,
      featureState: true,
      allowPlainValue: true,
      dataField: "zoom"
    },
    "heatmap-opacity": {
      title: "Heatmap opacity",
      shortTitle: "Opacity",
      dataType: "range",
      value: 1,
      options: [0, 1],
      type: "paint",
      expressions: true,
      featureState: true,
      allowPlainValue: true,
      dataField: "zoom"
    },
    "heatmap-radius": {
      title: "Heatmap radius",
      shortTitle: "Radius",
      dataType: "number",
      value: 5,
      type: "paint",
      expressions: true,
      featureState: true,
      allowPlainValue: true,
      dataField: ["zoom", "attribute"]
    },
    "heatmap-weight": {
      title: "Heatmap weight",
      shortTitle: "Weight",
      dataType: "range",
      value: 1,
      options: [0, 1],
      type: "paint",
      expressions: true,
      featureState: true,
      allowPlainValue: true,
      dataField: ["zoom", "attribute"]
    },

  },
};

export const colFields = [
  {code: "xs", icon: "fa fa-minus"},
  {code: "sm", icon: "fa fa-mobile"},
  {code: "md", icon: "fa fa-tablet"},
  {code: "lg", icon: "fa fa-laptop"},
  {code: "xl", icon: "fa fa-tv"},
];

export const attributeFields = {
  title: {title: "Column name", dataType: "string", value: ""},
  attributeId: {
    title: "Standard match",
    dataType: "smartSelect",
    options: [],
    value: null,
  },
  dataType: {
    title: "Data type",
    dataType: "list",
    value: {code: "string", title: "Text"},
    options: [
      {code: "string", title: "Text"},
      {code: "alphanumeric", title: "Alphanumeric"},
      {code: "integer", title: "Integer"},
      {code: "decimal", title: "Decimal"},
      {code: "date", title: "Date"},
      {code: "time", title: "Time"},
      {code: "editor", title: "Text editor"},
      {code: "boolean", title: "Boolean"},
      {code: "list", title: "Enumeration"},
      {code: "multilist", title: "Multi select"},
      {code: "checklist", title: "Checklist"},
      {code: "contextmenu", title: "Context menu"},
      {code: "button", title: "Button"},
      {code: "actionApis", title: "Action APIs"},
      {code: "dataSource", title: "Data source"},
      {code: "dataCollector", title: "Data collector"},
      {code: "photo", title: "Photo"},
      {code: "file", title: "File"},
      {code: "plant", title: "Plant"},
      {code: "color", title: "Color"},
      {code: "document", title: "Document"},
      {code: "computed", title: "Computed"},
      {code: "gallery", title: "Gallery"},
      {code: "stream", title: "Stream"}
    ],
  },
  defaultValue: {title: "Default value", dataType: "string", value: ""},
  isRequired: {title: "Required", dataType: "boolean", value: false},
  isEditable: {title: "Editable", dataType: "boolean", value: true},
  isVisible: {title: "Visible", dataType: "boolean", value: true},
  isSearchDefault: {title: "Active by default on search", dataType: "boolean", value: false},
  responsiveCols: {title: "Column span", dataType: "columns"},
  showIn: {
    title: "Show in",
    dataType: "multilist",
    options: [
      {value: "sidebar", label: "Sidebar"},
      {value: "modal", label: "Modal"},
      {value: "popup", label: "Popup"},
      {value: "floating", label: "Floating"},
    ],
    value: [],
  },
  plantTypes: ["leaf", "flower", "fruit", "bark"],
  properties: {},
};

export const attributeDataSourceSettings = [
  {
    title: "Nomenclature document",
    value: {
      endpoint: "documents/nomenclature-list?documentCategoryId=16",
      endpointMethod: "post",
      optionLabel: "title",
      optionValue: "documentId",
      relatedAttributes: [], // fill it with attributes that will filter item endpoint
      detailEndpoint: "documents/nomenclature?documentId=${documentId}",
      detailEndpointMethod: "get",
      versionData: false,
    },
  },
  {
    title: "Report",
    value: {
      endpoint: "documents/document-list?documentCategoryId=49",
      endpointMethod: "post",
      optionLabel: "title",
      optionValue: "documentId",
      relatedAttributes: [], // fill it with attributes that will filter item endpoint
      detailEndpoint: "documents/full?documentId=${documentId}",
      detailEndpointMethod: "get",
      versionData: false,
    },
  },
];

export function normalizeStyle(style, layerType, propertyType, overwriteValues) {
  if (!style) style = drawRandomStyle[layerType] || {};
  let normalized = {};
  for (let prop in style) {
    let value = style[prop];
    let skipProp = false;
    if (
      mapboxStyles[layerType].hasOwnProperty(prop) &&
      mapboxStyles[layerType][prop].type === propertyType &&
      (value || (prop.includes("opacity") && typeof value !== "undefined"))
    ) {
      switch (prop) {
        case "text-font":
          if (value && Array.isArray(value) && !value.length) skipProp = true;
          break;
        case "text-field":
          if (!Array.isArray(value)) {
            value = ["get", String(value)];
          }
          break;
        case "line-dasharray":
          if (!(Array.isArray(value) && value.length)) {
            skipProp = true
          }
          break;
        case "line-gradient":
          if (!value) skipProp = true
          break;
        case "visibility":
          value = Boolean(value) === true ? "visible" : "none";
          break;
        default:
          value = getMapboxPropertyValue(value, prop);
          break;
      }
      if (value !== "undefined" && !skipProp) {
        normalized[prop] = value;
      }
      if (overwriteValues && overwriteValues.hasOwnProperty(prop) && !skipProp) {
        normalized[prop] = overwriteValues[prop];
      }
    }
  }
  //console.log(type + '(' + shapeType + '): ', normalized)
  return normalized;
}

export function filterMapboxPropsByType(object, layerType, propertyType) {
  let filtered = {};
  for (let prop in object) {
    if (layerType) {
      if (
        mapboxStyles[layerType] &&
        mapboxStyles[layerType][prop] &&
        mapboxStyles[layerType][prop].type === propertyType
      ) {
        /*switch (prop) {
          case 'line-dasharray':
            if (Array.isArray(object[prop])) {
              let lastItem = object[prop].pop();
              if (Array.isArray(lastItem) && lastItem.length)
                filtered[prop] = object[prop];
            }
            else filtered[prop] = object[prop];
            break;
          case 'line-gradient':
            if (Array.isArray(object[prop])) {
              let lastItem = object[prop].pop();
              if (lastItem)
                filtered[prop] = object[prop];
            }
            else filtered[prop] = object[prop];
            break;
          default:
            filtered[prop] = object[prop];
        }*/
        filtered[prop] = object[prop];

      }
    } else {
      const mapboxProp = deepSearch(mapboxStyles, prop)[0];
      if (mapboxProp && mapboxProp.type === propertyType) {
        filtered[prop] = object[prop];
      }
    }
  }
  return filtered;
}

export const zoomLevelScale = [
  1000000000, 500000000, 200000000, 50000000, 25000000, 12500000, 6500000, 3000000, 1500000, 750000, 400000, 200000,
  100000, 50000, 25000, 12500, 5000, 2500, 1500, 750, 500, 250, 100, 0,
];

export function base64ToBlob(base64) {
  let binaryString = window.atob(base64);
  let binaryLen = binaryString.length;

  let ab = new ArrayBuffer(binaryLen);
  let ia = new Uint8Array(ab);
  for (let i = 0; i < binaryLen; i++) {
    ia[i] = binaryString.charCodeAt(i);
  }
  return ia;
}

export function DataURIToBlob(dataURI) {
  const splitDataURI = dataURI.split(",");
  const byteString = splitDataURI[0].indexOf("base64") >= 0 ? atob(splitDataURI[1]) : decodeURI(splitDataURI[1]);
  const mimeString = splitDataURI[0].split(":")[1].split(";")[0];

  const ia = new Uint8Array(byteString.length);
  for (let i = 0; i < byteString.length; i++) ia[i] = byteString.charCodeAt(i);

  return new Blob([ia], {type: mimeString});
}

export function b64toBlob(b64Data, contentType = '', sliceSize = 512) {
  const byteCharacters = atob(b64Data);
  const byteArrays = [];

  for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
    const slice = byteCharacters.slice(offset, offset + sliceSize);

    const byteNumbers = new Array(slice.length);
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);
    byteArrays.push(byteArray);
  }

  return new Blob(byteArrays, {type: contentType});
}

export function pointFeature(coordinates, properties) {
  const depth = getArrayDepth(coordinates);
  return {
    type: "Feature",
    properties: properties || {},
    geometry: {
      type: depth === 1 ? "MultiPoint" : "Point",
      coordinates: [coordinates],
    },
  };
}

export function lineStringFeature(coordinates, properties) {
  const depth = getArrayDepth(coordinates);
  return {
    type: "Feature",
    properties: properties || {},
    geometry: {
      type: depth === 2 ? "MultiLineString" : "LineString",
      coordinates: [coordinates],
    },
  };
}

export function polygonFeature(coordinates, properties) {
  const depth = getArrayDepth(coordinates);
  return {
    type: "Feature",
    properties: properties || {},
    geometry: {
      type: depth === 3 ? "MultiPolygon" : "Polygon",
      coordinates: [coordinates],
    },
  };
}

export function getArrayDepth(value) {
  return Array.isArray(value) ? 1 + Math.max(0, ...value.map(getArrayDepth)) : 0;
}

export function featureCollection(features) {
  return {
    type: "FeatureCollection",
    features: features,
  };
}

export function distanceLabelFormat(length) {
  return length > 1000 ? (length / 1000).toLocaleString() + " km" : length.toLocaleString() + " m";
}

export function areaLabelFormat(area) {
  if (area > 1000000) {
    //@ts-ignore
    area = (area / 1000000).toLocaleString('ro-RO');
    return `${area} kmp`;
  } else {
    //@ts-ignore
    area = area.toLocaleString('ro-RO');
    return `${area} mp`;
  }
}

export function perimeterLabelFormat(perimeter) {
  if (perimeter > 1000) {
    //@ts-ignore
    perimeter = roundToDecimal((perimeter / 1000), 2).toLocaleString('ro-RO');
    return `${perimeter} km`;
  } else {
    //@ts-ignore
    perimeter = roundToDecimal(perimeter, 2).toLocaleString('ro-RO');
    return `${perimeter} m`;
  }
}

export function getDrawingTypeByLayer(document) {
  if (document && document.properties && document.properties.shapeType && document.properties.shapeType.title) {
    return /point/gi.test(document.properties.shapeType.title)
      ? "point"
      : /polygon/gi.test(document.properties.shapeType.title)
        ? "polygon"
        : /line/gi.test(document.properties.shapeType.title)
          ? "line"
          : "raster";
  }
  return "raster";
}

export function getDrawingModeByLayer(layer) {
  if (layer && layer.properties && layer.properties.shapeType && layer.properties.shapeType.title) {
    return /point/gi.test(layer.properties.shapeType.title)
      ? "draw_point"
      : /polygon/gi.test(layer.properties.shapeType.title)
        ? "draw_polygon"
        : "draw_line_string";
  }
  return "select";
}

export function getLayerStyleProperty(layer, styleProperty, fallback, checkExpression) {
  let layerStyle = layer?.properties?.style ? layer.properties.style : null;
  let styleExpression = layer?.properties?.styleExpression ? layer.properties.styleExpression : null;
  if (checkExpression && styleExpression && styleExpression.hasOwnProperty(styleProperty)) {
    return styleExpression[styleProperty];
  } else if (layerStyle && layerStyle.hasOwnProperty(styleProperty)) {
    return layerStyle[styleProperty];
  } else {
    return fallback;
  }
}

export function getLayerLabelCss(style) {
  if (!style) return "";
  let css = [];
  for (let prop in style) {
    switch (prop) {
      case "text-color":
        css.push(`color: ${style[prop] || "#000000"}`);
        break;
      case "text-transform":
        css.push(`text-transform: ${style[prop] || "none"}`);
        break;
      case "text-size":
        css.push(`font-size: ${style[prop] || 12}px`);
        break;
      case "text-opacity":
        css.push(`opacity: ${style[prop] || 1}`);
        break;
    }
  }
  return css.length ? css.join(";") : "";
}

export function objectToString(obj) {
  let result = "";
  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      result += `${key}: ${obj[key]}; `;
    }
  }
  return result;
}

export function getLayerShapeCss(layer, style) {
  let css = [];
  if (!layer || !layer.properties) return "";
  const shape = getDrawingTypeByLayer(layer);
  let layerStyle = layer.properties.style;
  if (style) {
    layerStyle = style;
  }
  for (let prop in layerStyle) {
    switch (shape) {
      case "point":
        if (/icon-image/.test(prop) && layerStyle[prop]) {
          css.push(`background-image: url("${layerStyle[prop]}")`);
          css.push(`background-size: contain`);
          css.push(`background-repeat: no-repeat`);
          css.push(`background-position: center`);
          css.push(`background-color: transparent`);
          css.push(`border-color: transparent`);
        } else {
          if (/stroke-color/.test(prop)) {
            css.push(`border-color: ${layerStyle[prop]}`);
          }
          if (/stroke-width/.test(prop)) {
            css.push(`border-width: ${layerStyle[prop]}px`);
          }
          try {
            if (/circle-color/.test(prop) && !layerStyle.hasOwnProperty("circle-opacity")) {
              css.push(`background-color: ${hexToRgba(layerStyle[prop], 1)}`);
            } else if (/circle-opacity/.test(prop) && layerStyle["circle-color"]) {
              css.push(`background-color: ${hexToRgba(layerStyle["circle-color"], layerStyle[prop])}`);
            }
          } catch (e) {
          }
        }
        break;
      case "polygon":
        if (/fill-outline-color/.test(prop)) {
          css.push(`border-color: ${layerStyle[prop]}`);
        }
        if (/line-color/.test(prop)) {
          css.push(`border-color: ${layerStyle[prop]}`);
        }
        if (/line-width/.test(prop)) {
          css.push(`border-width: ${layerStyle[prop] / 2}px`);
        }

        try {
          if (/fill-pattern/.test(prop) && layerStyle[prop]) {
            css.push(`background-image: url("${layerStyle[prop]}")`);
            css.push(`background-size: contain`);
            css.push(`background-repeat: no-repeat`);
            css.push(`background-position: center`);
            css.push(`background-color: transparent`);
          } else if (
            /fill-color|fill-extrusion-color/.test(prop) &&
            !layerStyle.hasOwnProperty("fill-opacity")
          ) {
            css.push(`background-color: ${hexToRgba(layerStyle[prop], 1)}`);
          } else if (/fill-opacity/.test(prop) && layerStyle["fill-color"]) {
            css.push(`background-color: ${hexToRgba(layerStyle["fill-color"], layerStyle[prop])}`);
          } else if (/fill-opacity/.test(prop) && layerStyle["fill-extrusion-color"]) {
            css.push(
              `background-color: ${hexToRgba(layerStyle["fill-extrusion-color"], layerStyle[prop])}`
            );
          }
        } catch (e) {
        }
        break;
      case "line":
        if (/line-pattern/.test(prop) && layerStyle[prop]) {
          css.push(`background-image: url("${layerStyle[prop]}")`);
          if (layerStyle && layerStyle.hasOwnProperty("line-width"))
            css.push(`background-size: ${layerStyle["line-width"]}px;`);
          else css.push(`background-size: contain`);
          css.push(`background-repeat: no-repeat`);
          css.push(`background-position: center`);
          css.push(`background-color: transparent`);
        } else if (/line-color/.test(prop)) {
          css.push(`border-color: ${layerStyle[prop]}`);
        }
        if (/line-width/.test(prop)) {
          css.push(`border-width: ${layerStyle[prop] / 2}px`);
        }
        if (/line-dasharray/.test(prop) && layerStyle[prop] && layerStyle[prop].length === 2) {
          css.push(`border-style: dashed`);
        }
        break;
    }
  }
  //console.log('style to css(' + shape + ')', layerStyle, css.join(";"))
  return css.length ? css.join(";") : "";
}

export function getLayerBoundCoordinates(layer) {
  try {
    return JSON.parse(layer.locationCoordinates.bounds);
  } catch (e) {
  }
  if (
    layer.locationCoordinates &&
    layer.locationCoordinates.sLongitude &&
    layer.locationCoordinates.sLatitude &&
    layer.locationCoordinates.nLongitude &&
    layer.locationCoordinates.nLatitude
  ) {
    return [
      [layer.locationCoordinates.sLongitude, layer.locationCoordinates.sLatitude],
      [layer.locationCoordinates.nLongitude, layer.locationCoordinates.nLatitude],
    ];
  }
  else if(Array.isArray(layer?.locationCoordinates?.bounds)) return layer.locationCoordinates.bounds;
  else return false;
}

export function getCoordinatesFromGeometry(coordinates) {
  if (coordinates && Array.isArray(coordinates)) {
    for (let x = 0; x < coordinates.length; x++) {
      const point = coordinates[x];
      if (Array.isArray(point)) {
        return getCoordinatesFromGeometry(point);
      } else {
        return coordinates;
      }
    }
  } else {
    return coordinates;
  }
}

export function getAllCoordinatesFromGeometry(coordinates) {
  if (coordinates.length > 1) {
    return coordinates;
  } else {
    return getAllCoordinatesFromGeometry(coordinates[0]);
  }
}

export function getLayerContextMenu(layer, attributes, metadatas, attributesRights) {
  if (layer && layer.properties && layer.properties.header) {
    let options = [];
    for (let key in layer.properties.header) {
      const opt = layer.properties.header[key];
      if (opt.dataType && opt.dataType.code === 'contextmenu' && opt.options && opt.options.length) {
        for (let item of opt.options) {
          const title = opt?.title?.toLowerCase();

          const hasMetadatas = metadatas?.length > 0;
          const hasAttributes = attributesRights?.length > 0;

          const metadataMatch = hasMetadatas && metadatas.some((x) => x?.toLowerCase() === title);

          const attributeMatch = hasAttributes && attributesRights.some((x) => x?.toLowerCase() === title);

          if (
            // neither exists → allow
            (!hasMetadatas && !hasAttributes) ||
            // only metadatas exists → check metadatas
            (hasMetadatas && !hasAttributes && metadataMatch) ||
            // only attributes exists → check attributes
            (!hasMetadatas && hasAttributes && attributeMatch) ||
            // both exist → either can match (or change to && if needed)
            (hasMetadatas && hasAttributes && (metadataMatch || attributeMatch))
          ) {
            if (!item?.properties?.visibilityTriggers || !item.properties.visibilityTriggers.length) {
              options.push(item);
            } else {
              let isVisible = true;
              for (let trigger of item.properties.visibilityTriggers) {
                try {
                  isVisible = eval(trigger); // use attributes parameter
                } catch (e) {
                  isVisible = false;
                }
              }
              if (isVisible) {
                options.push(item);
              }
            }
          }
        }
      }
    }
    return options;
  }
  return [];
}

export function sortKeys(array, keys) {
  keys = keys || {};
  const obLen = function (obj) {
    let size = 0,
      key;
    for (key in obj) {
      if (obj.hasOwnProperty(key)) size++;
    }
    return size;
  };
  const obIx = function (obj, ix) {
    let size = 0,
      key;
    for (key in obj) {
      if (obj.hasOwnProperty(key)) {
        if (size == ix) return key;
        size++;
      }
    }
    return false;
  };

  const keySort = function (a, b, d) {
    d = d !== null ? d : 1;
    if (a == b) return 0;
    else if (a === null) return 1;
    else if (b == null) return -1;
    return a > b ? 1 * d : -1 * d;
  };

  const KL = obLen(keys);

  if (!KL) return array.sort(keySort);

  for (const k in keys) {
    // asc unless desc or skip
    keys[k] = keys[k] == "desc" || keys[k] == -1 ? -1 : keys[k] == "skip" || keys[k] === 0 ? 0 : 1;
  }

  array.sort(function (a, b) {
    let sorted = 0,
      ix = 0;

    while (sorted === 0 && ix < KL) {
      const k = obIx(keys, ix);
      if (k) {
        const dir = keys[k];
        sorted = keySort(a[k], b[k], dir);
        ix++;
      }
    }
    return sorted;
  });
  return array;
}

export function denseRank(array, keys) {
  let rowNo = 1;
  if (array && array.length)
    array.forEach((item, index) => {
      if (index === 0) {
        item.priority = 1;
      } else {
        let a = array[index - 1];
        let b = item;
        if (a && b) {
          let match = true;
          if (keys)
            keys.forEach((key) => {
              if (a[key] !== b[key]) match = false;
            });

          if (match) {
            rowNo++;
          } else rowNo = 1;

          b.priority = rowNo;
        } else if (b) {
          b.priority = rowNo;
        }
      }
    });
  return array;
}

export function getAscendants(array, id, fieldName, parentFieldName, result) {
  if (array && array.length) {
    let item = array.find((x) => x[fieldName] === id);
    if (item) {
      result.push(item);
      if (item[parentFieldName]) getAscendants(array, item[parentFieldName], fieldName, parentFieldName, result);
    }
  }
  return result;
}

export function removeItem(array, element) {
  const index = array.indexOf(element);
  if (index !== -1) {
    array.splice(index, 1);
  }
  return array;
}

const toString = Object.prototype.toString;
export const clone = (obj) => {
  let rv;

  switch (typeof obj) {
    case "object":
      if (obj === null) {
        rv = null;
      } else {
        switch (toString.call(obj)) {
          case "[object Array]":
            rv = obj.map(clone);
            break;
          case "[object Date]":
            rv = new Date(obj);
            break;
          case "[object RegExp]":
            rv = new RegExp(obj);
            break;
          default:
            rv = Object.keys(obj).reduce(function (prev, key) {
              prev[key] = clone(obj[key]);
              return prev;
            }, {});
            break;
        }
      }
      break;
    default:
      rv = obj;
      break;
  }
  return rv;
};

export function smartSelectDefaultTextValue(list, fieldName, value) {
  if (!value) return "";
  let textValue = [];
  if (list)
    for (let x = 0; x < list.length; x++) {
      if (
        value &&
        Object.prototype.toString.call(value) === "[object Array]" &&
        value.indexOf(list[x][fieldName]) > -1
      ) {
        textValue.push(list[x].title);
      } else if (value && typeof value === "object" && value[fieldName] === list[x][fieldName]) {
        textValue.push(list[x].title);
      } else if (list[x][fieldName] == value) {
        if (list[x].title) {
          textValue.push(list[x].title);
        } else if (list[x].code) {
          textValue.push(list[x].code);
        } else {
          textValue.push(list[x][fieldName]);
        }
      }
    }
  return textValue.join(", ");
}

export function smartSelectDefaultTextValueSliced(list, fieldName, value, limit = 5) {
  if (!value) return "";
  let textValue = [];
  for (let x = 0; x < list.length; x++) {
    if (
      value &&
      Object.prototype.toString.call(value) === "[object Array]" &&
      value.indexOf(list[x][fieldName]) > -1
    ) {
      textValue.push(list[x].title);
    } else if (value && typeof value === "object" && value[fieldName] === list[x][fieldName]) {
      textValue.push(list[x].title);
    } else if (list[x][fieldName] == value) {
      if (list[x].title) {
        textValue.push(list[x].title);
      } else if (list[x].code) {
        textValue.push(list[x].code);
      } else {
        textValue.push(list[x][fieldName]);
      }
    }
  }
  return textValue.length <= limit ? textValue.join(", ") : `${textValue.slice(0, limit).join(", ")}...`;
}

export const filterObjectValues = (obj, filterKeys) => {
  return Object.keys(obj)
    .filter((key) => filterKeys.includes(key))
    .reduce((tmp, key) => {
      tmp[key] = obj[key];
      return tmp;
    }, {});
};

export function getStyleExpression(rules, expression = [], styles = [], hoverStyles = [], focusStyles = [], highlightStyles = [], fallbackStyles = []) {
  if (!expression) expression = [];
  if (rules.length) {
    for (let x = 0; x < rules.length; x++) {
      const rule = rules[x];

      if (rule.rules.length) {
        expression.push(
          ...getFormula(rule, x === 0, x === rules.length - 1, true),
          getStyleExpression(rule.rules, [], styles, hoverStyles, focusStyles, highlightStyles, fallbackStyles).expression
        );
        if (rule.style && Object.keys(rule.style).length > 0) {
          fallbackStyles.push(rule.style);
        }
      } else {
        expression.push(
          ...getFormula(rule, x === 0, x === rules.length - 1, false)
        );
      }

      if (rule.style && Object.keys(rule.style).length > 0) {
        styles.push(rule.style);
      }
      if (rule?.styleHover?.isActive && Object.keys(rule.styleHover).length > 0) {
        hoverStyles.push(rule.styleHover);
      }
      if (rule?.styleFocus?.isActive && Object.keys(rule.styleFocus).length > 0) {
        focusStyles.push(rule.styleFocus);
      }
      if (rule?.styleHighlight?.isActive && Object.keys(rule.styleHighlight).length > 0) {
        highlightStyles.push(rule.styleHighlight);
      }
    }
  }

  //add fallback value if the expression starts with `case` and is not ending correctly
  if (expression[0] === "case" && typeof expression[expression.length - 1] === "object") {
    expression.push(`{{fallback}}`);
  }

  return {expression: expression, styles: styles, hoverStyles: hoverStyles, focusStyles: focusStyles, highlightStyles: highlightStyles, fallbackStyles: fallbackStyles};
}

/**
 * Get style expression without rules, based on styleHover and/or styleFocus/styleHighlight if exists
 * @param layerProperties
 * @returns {{}|*}
 */
export function getSimpleStyleExpression(layerProperties) {
  if (!layerProperties?.styleHover?.isActive && !layerProperties?.styleFocus?.isActive && !layerProperties?.styleHighlight?.isActive) {
    const style = {};
    for (let key in layerProperties.style) {
      if (layerProperties.style[key] !== "") {
        style[key] = getMapboxPropertyValue(layerProperties.style[key], key);
      }
    }
    return style;
  } else {
    let expression = {};
    const featureStateProps = getAllMapboxFeatureStateStyleProperties()

    for (let key in layerProperties.style) {
      expression[key] = ['case'];
      if (
        layerProperties?.styleHover?.isActive &&
        layerProperties?.styleHover?.hasOwnProperty(key) &&
        layerProperties?.styleHover[key] !== "" &&
        featureStateProps.hasOwnProperty(key)
      ) {
        expression[key].push(['boolean', ['feature-state', 'hover'], false]);
        expression[key].push(getMapboxPropertyValue(layerProperties.styleHover[key], key));
      }

      if (
        layerProperties?.styleFocus?.isActive &&
        layerProperties?.styleFocus?.hasOwnProperty(key) &&
        layerProperties?.styleFocus[key] !== "" &&
        featureStateProps.hasOwnProperty(key)
      ) {
        expression[key].push(['boolean', ['feature-state', 'focus'], false]);
        expression[key].push(getMapboxPropertyValue(layerProperties.styleFocus[key], key));
      }

      if (
        layerProperties?.styleHighlight?.isActive &&
        layerProperties?.styleHighlight?.hasOwnProperty(key) &&
        layerProperties?.styleHighlight[key] !== "" &&
        featureStateProps.hasOwnProperty(key)
      ) {
        expression[key].push(['boolean', ['feature-state', 'highlight'], false]);
        expression[key].push(getMapboxPropertyValue(layerProperties.styleHighlight[key], key));
      }

      if (expression[key].length === 1 && layerProperties.style[key] !== "") {
        expression[key] = getMapboxPropertyValue(layerProperties.style[key], key);
      } else if (layerProperties.style[key] !== "") {
        expression[key].push(getMapboxPropertyValue(layerProperties.style[key], key));
      }
      if (expression[key].length === 1) {
        delete expression[key];
      }
    }
    return expression;
  }
}

export function getFormula(rule, isFirstRule, isLastRule, hasSubRules, isAnyOrAll) {
  let result = [];
  let formula = [];

  let allResult = [];
  if (rule?.all?.length) {
    for (let i = 0; i < rule?.all.length; i++) {
      allResult.push(...getFormula(rule.all[i], false, false, false, true));
    }
  }

  let anyResult = [];
  if (rule?.any?.length) {
    for (let i = 0; i < rule?.any.length; i++) {
      anyResult.push(...getFormula(rule.any[i], false, false, false, true));
    }
  }

  let isZoomRule = false;

  if (rule.operand1 && rule.operand1.code === "zoom") {
    isZoomRule = true;
    if (isFirstRule) {
      formula.push("step", ["zoom"], `{{fallback}}`);
    }
  } else {
    if (rule.operator) {
      switch (rule.operator) {
        case "index-of":
          formula.push(">");
          break;
        case "not-index-of":
          formula.push("==");
          break;
        default:
          formula.push(rule.operator);
          break;
      }
    }
  }

  if (rule.operand1 && rule.operand1.value) {
    switch (rule.operand1.code) {
      case "attribute":
        switch (rule.operator) {
          case "!":
            formula.push(["has", rule.operand1.value.title]);
            break;
          case "not-index-of":
          case "index-of":
            formula.push(["index-of", ["downcase", rule.operand2.value], ["downcase", ["get", rule.operand1.value.title]]], -1);
            break;
          default:
            formula.push(["get", rule.operand1.value.title]);
            break;
        }
        break;
      default:
        if (!isZoomRule) {
          switch (rule.operator) {
            case "not-index-of":
            case "index-of":
              // do nothing
              break;
            default:
              let val = rule.operand1.value;
              if (isNumeric(val)) {
                val = parseFloat(val);
              }
              formula.push(val);
              break;
          }
        }
        break;
    }
  }

  if (rule.operand2 && rule.operand2.value) {
    switch (rule.operand2.code) {
      case "attribute":
        switch (rule.operator) {
          case "not-index-of":
          case "index-of":
            // do nothing
            break;
          default:
            formula.push(["get", rule.operand2.value.title]);
            break;
        }
        break;
      default:
        switch (rule.operator) {
          case "not-index-of":
          case "index-of":
            // do nothing
            break;
          default:
            let val = rule.operand2.value;
            if (
              isNumeric(val) ||
              ((rule?.operand1?.code === "attribute" &&
                /integer|decimal/.test(rule?.operand1?.value?.dataType?.code)) ||
                rule?.operand1?.code === "zoom")
            ) {
              val = parseFloat(val);
            }
            formula.push(val);
            break;
        }
        break;
    }
  }

  if (isZoomRule) {
    result = formula;
    if (!hasSubRules) {
      let subResult = ['case'];

      if (rule?.styleHover?.isActive) {
        subResult.push(['boolean', ['feature-state', 'hover'], false]);
        subResult.push(`{{styleHover}}`);
      }

      if (rule?.styleFocus?.isActive) {
        subResult.push(['boolean', ['feature-state', 'focus'], false]);
        subResult.push(`{{styleFocus}}`);
      }

      if (rule?.styleHighlight?.isActive) {
        subResult.push(['boolean', ['feature-state', 'highlight'], false]);
        subResult.push(`{{styleHighlight}}`);
      }

      if (subResult.length > 1) {
        result.push([...subResult, `{{style}}`]);
      } else {
        result.push(`{{style}}`);
      }
    }
  } else if (isFirstRule) {
    if (!hasSubRules) {
      if (rule?.operator === "==") {
        result = ["case", [...formula, ["collator", {"case-sensitive": false, "diacritic-sensitive": false, "locale": "ro"}]]];
      } else if (allResult.length && anyResult.length) {
        result = ["case", ["all", ...allResult, formula], ["any", ...anyResult, formula]];
      } else if (allResult.length) {
        result = ["case", ["all", ...allResult, formula]];
      } else if (anyResult.length) {
        result = ["case", ["any", ...anyResult, formula]];
      } else {
        result = ["case", formula];
      }

      let subResult = ['case'];

      if (rule?.styleHover?.isActive) {
        subResult.push(['boolean', ['feature-state', 'hover'], false]);
        subResult.push(`{{styleHover}}`);
      }

      if (rule?.styleFocus?.isActive) {
        subResult.push(['boolean', ['feature-state', 'focus'], false]);
        subResult.push(`{{styleFocus}}`);
      }

      if (rule?.styleHighlight?.isActive) {
        subResult.push(['boolean', ['feature-state', 'highlight'], false]);
        subResult.push(`{{styleHighlight}}`);
      }

      if (subResult.length > 1) {
        result.push([...subResult, `{{style}}`]);
      } else if (!isAnyOrAll) {
        result.push(`{{style}}`);
      }

      if (isLastRule) {
        result.push(`{{fallback}}`); // fallback value
      }
    } else {
      if (rule?.operator === "==") {
        result = ["case", [...formula, ["collator", {"case-sensitive": false, "diacritic-sensitive": false, "locale": "ro"}]]];
      } else if (allResult.length && anyResult.length) {
        result = ["case", ["all", ...allResult, formula], ["any", ...anyResult, formula]];
      } else if (allResult.length) {
        result = ["case", ["all", ...allResult, formula]];
      } else if (anyResult.length) {
        result = ["case", ["any", ...anyResult, formula]];
      } else {
        result = ["case", formula];
      }
    }
  } else {
    if (!hasSubRules) {
      if (rule?.operator === "==") {
        result = [[...formula, ["collator", {"case-sensitive": false, "diacritic-sensitive": false, "locale": "ro"}]]];
      } else if (allResult.length && anyResult.length) {
        result = [["all", ...allResult, formula], ["any", ...anyResult, formula]];
      } else if (allResult.length) {
        result = [["all", ...allResult, formula]];
      } else if (anyResult.length) {
        result = [["any", ...anyResult, formula]];
      } else {
        result = [formula];
      }

      let subResult = ['case'];

      if (rule?.styleHover?.isActive) {
        subResult.push(['boolean', ['feature-state', 'hover'], false]);
        subResult.push(`{{styleHover}}`);
      }

      if (rule?.styleFocus?.isActive) {
        subResult.push(['boolean', ['feature-state', 'focus'], false]);
        subResult.push(`{{styleFocus}}`);
      }

      if (rule?.styleHighlight?.isActive) {
        subResult.push(['boolean', ['feature-state', 'highlight'], false]);
        subResult.push(`{{styleHighlight}}`);
      }

      if (subResult.length > 1) {
        result.push([...subResult, `{{style}}`]);
      } else if (!isAnyOrAll) {
        result.push(`{{style}}`);
      }

      if (isLastRule) {
        result.push(`{{fallback}}`); // fallback value
      }
    } else {
      if (rule?.operator === "==") {
        result = [[...formula, ["collator", {"case-sensitive": false, "diacritic-sensitive": false, "locale": "ro"}]]];
      } else if (allResult.length && anyResult.length) {
        result = [["all", ...allResult, formula], ["any", ...anyResult, formula]];
      } else if (allResult.length) {
        result = [["all", ...allResult, formula]];
      } else if (anyResult.length) {
        result = [["any", ...anyResult, formula]];
      } else {
        result = [formula];
      }
    }
  }

  return result;
}

export function getStyleFromExpressionAndStyles(
  expressionAndStyles,
  style = {},
  defaultStyle
) {
  if (expressionAndStyles.styles.length) {
    let allProps = [];
    for (let x = 0; x < expressionAndStyles.styles.length; x++) {
      for (let prop in expressionAndStyles.styles[x]) {
        if (allProps.indexOf(prop) === -1) {
          allProps.push(prop);
        }
      }
    }
    let skipProp = false;
    for (let x = 0; x < allProps.length; x++) {
      skipProp = false
      const prop = allProps[x];
      let expr = parseExpression(
        [...expressionAndStyles.expression],
        expressionAndStyles.styles,
        prop,
        0,
        defaultStyle,
        expressionAndStyles.hoverStyles,
        0,
        expressionAndStyles.focusStyles,
        0,
        expressionAndStyles.highlightStyles,
        0,
        expressionAndStyles.fallbackStyles,
        0
      ).expression;

      switch (prop) {
        case "line-dasharray":
          if (Array.isArray(expr)) {
            let lastItem = expr[expr.length - 1];
            if (!(Array.isArray(lastItem) && lastItem.length)) {
              skipProp = true
            }
          }
          break;
        case "line-gradient":
          if (Array.isArray(expr)) {
            let lastItem = expr[expr.length - 1];
            if (!lastItem)
              skipProp = true
          }
          break;
      }
      if (!skipProp)
        style[prop] = expr
    }
  }
  return style;
}

export function parseExpression(
  expression,
  styles,
  property,
  count = 0,
  defaultStyle = {},
  hoverStyles = [],
  countHover = 0,
  focusStyles = [],
  countFocus = 0,
  highlightStyles = [],
  countHighlight = 0,
  fallbackStyles = [],
  countFallback = 0
) {
  for (let x = 0; x < expression.length; x++) {
    if (Array.isArray(expression[x])) {
      const exp = parseExpression(
        [...expression[x]],
        styles,
        property,
        count,
        defaultStyle,
        hoverStyles,
        countHover,
        focusStyles,
        countFocus,
        highlightStyles,
        countHighlight,
        fallbackStyles,
        countFallback
      );
      expression[x] = exp.expression;
      count = exp.count || 0;
      countFallback = exp.countFallback || 0;
      countHover = exp.countHover || 0;
      countFocus = exp.countFocus || 0;
      countHighlight = exp.countHighlight || 0;
    } else if (expression[x] === "{{style}}" && styles[count]) {
      expression[x] = getMapboxPropertyValue(styles[count][property], property);
      count += 1;
    } else if (expression[x] === "{{styleHover}}" && hoverStyles[countHover]) {
      expression[x] = getMapboxPropertyValue(hoverStyles[countHover][property], property);
      countHover += 1;
    } else if (expression[x] === "{{styleFocus}}" && focusStyles[countFocus]) {
      expression[x] = getMapboxPropertyValue(focusStyles[countFocus][property], property);
      countFocus += 1;
    } else if (expression[x] === "{{styleHighlight}}" && highlightStyles[countHighlight]) {
      expression[x] = getMapboxPropertyValue(highlightStyles[countHighlight][property], property);
      countHighlight += 1;
    } else if (
      expression[x] === "{{fallback}}" &&
      (fallbackStyles[countFallback] || defaultStyle || styles.length)
    ) {
      if (fallbackStyles[countFallback]) {
        expression[x] = getMapboxPropertyValue(fallbackStyles[countFallback][property], property);
        countFallback += 1;
        count += 1;
      } else if (
        defaultStyle &&
        defaultStyle.hasOwnProperty(property) &&
        defaultStyle[property] !== ""
      ) {
        expression[x] = getMapboxPropertyValue(
          defaultStyle[property],
          property
        );
      } else {
        expression[x] = getMapboxPropertyValue(styles[0][property], property);
      }
    }
  }
  return {expression: [...expression], count: count, countFallback, countHover, countFocus, countHighlight};
}

export function getMapboxPropertyValue(value, property) {
  const mbp = deepSearch(mapboxStyles, property)[0];

  if (value === undefined) return mbp ? mbp.value : null;

  if (Array.isArray(value)) {
    return value;
  }

  if (mbp) {
    switch (mbp.dataType) {
      case "range":
      case "number":
        return Number(value);
      case "boolean":
        return Boolean(value);
      case "array":
        return !value.length ? null : value;
      default:
        if (/fill-extrusion-height|fill-extrusion-base/.test(property)) return ["get", value];
        else return value;
    }
  }
  return value;
}

export function isNumeric(n) {
  return !isNaN(parseFloat(n)) && isFinite(parseFloat(n));
}

export function fixStyleValues(style) {
  let fixedStyle = {};
  for (let prop in style) {
    fixedStyle[prop] = getMapboxPropertyValue(style[prop], prop);
  }
  return fixedStyle;
}

export const isObject = (item) => {
  return item && typeof item === "object" && !Array.isArray(item);
};

export const merge = (target, ...sources) => {
  if (!sources.length) return target;
  const source = sources.shift();

  if (isObject(target) && isObject(source)) {
    for (const key in source) {
      if (isObject(source[key])) {
        if (!target[key])
          Object.assign(target, {
            [key]: {},
          });
        merge(target[key], source[key]);
      } else {
        Object.assign(target, {
          [key]: source[key],
        });
      }
    }
  }
  return merge(target, ...sources);
};

export function extractValueFromZoomStyleRules(styleRules, zoomLevel, property, isFirstRun) {
  const rule = isFirstRun
    ? styleRules.find(
      (r) => r.operator1 && r.operator1.code === "zoom" && r.operator2 && r.operator2.value === zoomLevel
    )
    : styleRules.find((r) => r.style && r.style.hasOwnProperty(property));

  if (rule && rule.style && rule.style.hasOwnProperty(property)) {
    return rule.style[property];
  } else if (rule && rule.rules && rule.rules.length) {
    return extractValueFromZoomStyleRules(rule.rules, zoomLevel, property, false);
  } else {
    return "";
  }
}

export function parseExpressionImages(expression, images, sdf) {
  if (expression)
    for (let x = 0; x < expression.length; x++) {
      if (Array.isArray(expression[x])) {
        expression[x] = parseExpressionImages([...expression[x]], images, sdf).expression;
      } else if (/^http/.test(expression[x])) {
        const imageId = "syncmap_image_" + parseInt(String(Math.random() * 10000));
        images.push({
          url: expression[x],
          id: imageId,
          sdf: sdf,
        });
        expression[x] = imageId;
      } else if (!expression[x] && !(expression.length > 1 && expression[1] && expression[1].length && expression[1][0] === 'feature-state')) {
        const imageId = "syncmap_image_" + parseInt(String(Math.random() * 10000));
        images.push({
          url: null,
          id: imageId,
          sdf: sdf,
        });
        expression[x] = imageId;
      }
    }
  return {
    expression: expression ? [...expression] : undefined,
    images: images,
  };
}

export function parseDashExpression(expression, values) {
  if (expression)
    for (let x = 0; x < expression.length; x++) {
      if (Array.isArray(expression[x])) {
        if (expression[x].length === 2 && typeof expression[x][0] === "number" && typeof expression[x][1] === "number")
          values.push(expression[x]);
        else
          expression[x] = parseDashExpression([...expression[x]], values).expression;
      }
    }
  return {
    expression: expression ? [...expression] : undefined,
    values: values,
  };
}

export function parseColorExpression(expression, values) {
  if (expression)
    for (let x = 0; x < expression.length; x++) {
      if (Array.isArray(expression[x])) {
        expression[x] = parseColorExpression([...expression[x]], values).expression;
      } else if (/^#/.test(expression[x])) {
        values.push(expression[x]);
      }
    }
  return {
    expression: expression ? [...expression] : undefined,
    values: values,
  };
}

export function getExpressionImages(expression, images) {
  if (expression)
    for (let x = 0; x < expression.length; x++) {
      if (Array.isArray(expression[x])) {
        images = getExpressionImages([...expression[x]], images);
      } else if (/^http/.test(expression[x]) && images.indexOf(expression[x]) === -1) {
        images.push(expression[x]);
      }
    }
  return images;
}

export const mapboxDrawStyle = [
  // ACTIVE (being drawn)
  // line stroke
  {
    id: "gl-draw-line",
    type: "line",
    filter: ["all", ["==", "$type", "LineString"], ["!=", "mode", "static"]],
    layout: {
      "line-cap": "round",
      "line-join": "round",
    },
    paint: {
      "line-color": "#D20C0C",
      "line-dasharray": [0.2, 2],
      "line-width": 3,
    },
  },
  // polygon fill
  {
    id: "gl-draw-polygon-fill",
    type: "fill",
    filter: ["all", ["==", "$type", "Polygon"], ["!=", "mode", "static"]],
    paint: {
      "fill-color": "#D20C0C",
      "fill-outline-color": "#D20C0C",
      "fill-opacity": 0.1,
    },
  },
  // polygon mid points
  {
    id: "gl-draw-polygon-midpoint",
    type: "circle",
    filter: ["all", ["==", "$type", "Point"], ["==", "meta", "midpoint"]],
    paint: {
      "circle-radius": 5,
      "circle-color": "#fbb03b",
    },
  },
  // polygon outline stroke
  // This doesn't style the first edge of the polygon, which uses the line stroke styling instead
  {
    id: "gl-draw-polygon-stroke-active",
    type: "line",
    filter: ["all", ["==", "$type", "Polygon"], ["!=", "mode", "static"]],
    layout: {
      "line-cap": "round",
      "line-join": "round",
    },
    paint: {
      "line-color": "#D20C0C",
      "line-dasharray": [0.2, 2],
      "line-width": 3,
    },
  },
  // vertex point halos
  {
    id: "gl-draw-polygon-and-line-vertex-halo-active",
    type: "circle",
    filter: ["all", ["==", "meta", "vertex"], ["==", "$type", "Point"], ["!=", "mode", "static"]],
    paint: {
      "circle-radius": 7,
      "circle-color": "#FFF",
    },
  },
  // vertex points
  {
    id: "gl-draw-polygon-and-line-vertex-active",
    type: "circle",
    filter: ["all", ["==", "meta", "vertex"], ["==", "$type", "Point"], ["!=", "mode", "static"]],
    paint: {
      "circle-radius": 5,
      "circle-color": "#D20C0C",
    },
  },

  // INACTIVE (static, already drawn)
  // line stroke
  {
    id: "gl-draw-line-static",
    type: "line",
    filter: ["all", ["==", "$type", "LineString"], ["==", "mode", "static"]],
    layout: {
      "line-cap": "round",
      "line-join": "round",
    },
    paint: {
      "line-color": "#000",
      "line-width": 4,
    },
  },
  // polygon fill
  {
    id: "gl-draw-polygon-fill-static",
    type: "fill",
    filter: ["all", ["==", "$type", "Polygon"], ["==", "mode", "static"]],
    paint: {
      "fill-color": "#000",
      "fill-outline-color": "#000",
      "fill-opacity": 0.1,
    },
  },
  // polygon outline
  {
    id: "gl-draw-polygon-stroke-static",
    type: "line",
    filter: ["all", ["==", "$type", "Polygon"], ["==", "mode", "static"]],
    layout: {
      "line-cap": "round",
      "line-join": "round",
    },
    paint: {
      "line-color": "#000",
      "line-width": 4,
    },
  },
  {
    id: "gl-draw-point-point-stroke-inactive",
    type: "circle",
    filter: [
      "all",
      ["==", "active", "false"],
      ["==", "$type", "Point"],
      ["==", "meta", "feature"],
      ["!=", "mode", "static"],
    ],
    paint: {
      "circle-radius": 5,
      "circle-opacity": 1,
      "circle-color": "black",
    },
  },
  {
    id: "gl-draw-point-inactive",
    type: "circle",
    filter: [
      "all",
      ["==", "active", "false"],
      ["==", "$type", "Point"],
      ["==", "meta", "feature"],
      ["!=", "mode", "static"],
    ],
    paint: {
      "circle-radius": 3,
      "circle-color": "green",
    },
  },
  {
    id: "gl-draw-point-stroke-active",
    type: "circle",
    filter: ["all", ["==", "$type", "Point"], ["==", "active", "true"], ["!=", "meta", "midpoint"]],
    paint: {
      "circle-radius": 7,
      "circle-color": "blue",
    },
  },
  {
    id: "gl-draw-point-active",
    type: "circle",
    filter: ["all", ["==", "$type", "Point"], ["!=", "meta", "midpoint"], ["==", "active", "true"]],
    paint: {
      "circle-radius": 5,
      "circle-color": "red",
    },
  },
  {
    id: "label",
    type: "symbol",
    filter: ["all", ["==", "$type", "Point"], ["==", "meta", "label"]],
    layout: {
      "text-field": ["get", "label"],
    },
    paint: {
      "text-color": "red",
      "text-halo-width": 2,
      "text-halo-blur": 2,
      "text-halo-color": "#ffffff",
    },
  },
];

export function convertToRGB(colorHex) {
  if (!colorHex || colorHex.length !== 6) {
    return null;
  }

  const aRgbHex = colorHex.match(/.{1,2}/g);
  return [parseInt(aRgbHex[0], 16), parseInt(aRgbHex[1], 16), parseInt(aRgbHex[2], 16)];
}

export function roundToDecimal(number, precision) {
  precision = precision || 2;
  let factor = Math.pow(10, precision);
  let tempNumber = number * factor;
  let roundedTempNumber = Math.round(tempNumber);
  return roundedTempNumber / factor;
}

export function getRandomColor() {
  return (
    "#" +
    Math.floor(Math.random() * 16777215)
      .toString(16)
      .padStart(6, "0")
  );
}

export const drawDefaultStyle = {
  circle: {
    "circle-blur": 0.2,
    "circle-radius": 6,
    "circle-opacity": 0.65,
    "circle-color": "#ed6498",
    "circle-stroke-width": 2,
    "circle-stroke-color": "#3b0619",
    "circle-stroke-opacity": 0.75,
  },
  fill: {
    "fill-antialias": true,
    "fill-color": "#ed6498",
    "fill-opacity": 0.5,
    "fill-outline-color": "#3b0619",
  },
  line: {
    "line-blur": 0.2,
    "line-width": 5,
    "line-color": "#ed6498",
  },
};

export const drawRandomStyle = {
  circle: {
    "circle-blur": 0.2,
    "circle-radius": 6,
    "circle-opacity": 0.65,
    "circle-color": getRandomColor(),
    "circle-stroke-width": 2,
    "circle-stroke-color": getRandomColor(),
    "circle-stroke-opacity": 0.75,
  },
  fill: {
    "fill-antialias": true,
    "fill-color": getRandomColor(),
    "fill-opacity": 0.5,
    "fill-outline-color": getRandomColor(),
  },
  line: {
    "line-blur": 0.2,
    "line-width": 5,
    "line-color": getRandomColor(),
  },
};

export function getCleanVisibleLayers(layers) {
  if (!Array.isArray(layers)) return [];
  let clean = [];
  for (let item of layers) {
    clean.push({
      documentId: item.documentId,
      documentCategoryId: item.documentCategoryId
        ? item.documentCategoryId
        : item?.documentCategory?.documentCategoryId,
      children: item.children ? getCleanVisibleLayers(item.children) : [],
    });
  }
  return clean;
}

export function isProjectVisible(project, visibleLayers) {
  if (!visibleLayers) {
    return false;
  } else {
    let isVisible = true;
    if (project.children && project.children.length) {
      for (let layer of project.children) {
        if (!findDocumentRecursively(visibleLayers, layer.documentId)) {
          isVisible = false;
          break;
        }
      }
    } else isVisible = false;
    return isVisible;
  }
}

export function isDocumentVisible(document, visibleDocuments) {
  if (visibleDocuments) if (findDocumentRecursively(visibleDocuments, document.documentId)) return true;
  return false;
}

export function removeElementByPropertyValue(object, property, value) {
  if (object.children) {
    for (let i = 0; i < object.children.length; i++) {
      if (object.children[i][property] === value) {
        object.children = object.children.filter((item, index) => index !== i);
      } else if (object.children[i].children) {
        removeElementByPropertyValue(object.children[i], property, value);
      } else if (object.children[i]?.properties?.columns) {
        for (let col of object.children[i]?.properties?.columns) {
          removeElementByPropertyValue(col, property, value);
        }
      }
    }
  }
  return object;
}

export function getNestedList(arr, primaryFieldName, parentFieldName, parentId, depth) {
  const out = [];
  if (arr) {
    const array =
      typeof parentId !== "undefined" && !parentId
        ? arr.filter((x) => !x[parentFieldName])
        : arr.filter(
          (x) =>
            (typeof parentId !== "undefined" && x[parentFieldName] === parentId) ||
            (typeof parentId === "undefined" &&
              typeof depth !== "undefined" &&
              typeof x.depth !== "undefined" &&
              x.depth === depth) ||
            (typeof parentId === "undefined" &&
              typeof x.depth == "undefined" &&
              x[parentFieldName] === null)
        );
    if (array.length > 0)
      for (let i = 0; i < array.length; i++) {
        const children = getNestedList(arr, primaryFieldName, parentFieldName, array[i][primaryFieldName]);

        if (children && children.length > 0) {
          array[i].children = children;
        }
        out.push(array[i]);
      }
  }
  return out;
}

export function isJsonString(str) {
  if (typeof str !== "string") {
    return false;
  }
  try {
    const json = JSON.parse(str);
    return typeof json === "object";
  } catch (e) {
    return false;
  }
}

export function dataURItoBlob(dataURI) {
  // convert base64 to raw binary data held in a string
  // doesn't handle URLEncoded DataURIs - see SO answer #6850276 for code that does this
  const byteString = atob(dataURI.split(",")[1]);

  // separate out the mime component
  const mimeString = dataURI.split(",")[0].split(":")[1].split(";")[0];

  // write the bytes of the string to an ArrayBuffer
  const ab = new ArrayBuffer(byteString.length);

  // create a view into the buffer
  const ia = new Uint8Array(ab);

  // set the bytes of the buffer to the correct values
  for (let i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i);
  }

  // write the ArrayBuffer to a blob, and you're done
  return new Blob([ab], {type: mimeString});
}

/**
 *
 * @param queryParams
 * @param queryCombinationsOfValuesBySeparator
 * @returns {*[]}
 */
export function queryParamsToQueryFilter(queryParams, queryCombinationsOfValuesBySeparator) {
  if (!queryParams || typeof queryParams !== "object") return [];

  let queryFilter = [];
  for (let key in queryParams) {
    if (key !== "api_key" && queryParams[key] != "") {
      let query = {
        field: key,
        operator: "OR",
        rules: [],
      };

      let queryValue = queryParams[key] && typeof queryParams[key] === 'string' ? queryParams[key].split("|") : [];
      for (let val of queryValue) {
        query.rules.push({
          field: key,
          dataType: {
            code: "string",
            title: "Text",
          },
          operator: "equals",
          value: val,
        });
        if (
          val &&
          queryCombinationsOfValuesBySeparator &&
          val.split(queryCombinationsOfValuesBySeparator).length > 1
        ) {
          for (let i = val.split(queryCombinationsOfValuesBySeparator).length - 1; i > 0; i--)
            query.rules.push({
              field: key,
              dataType: {
                code: "string",
                title: "Text",
              },
              operator: "equals",
              value: val
                .split(queryCombinationsOfValuesBySeparator)
                .slice(0, i)
                .join(queryCombinationsOfValuesBySeparator),
            });
        }
      }
      if (query.rules.length) {
        queryFilter.push(query);
      }
    }
  }
  return queryFilter;
}

/**
 * Transforms an object in URL query
 * @param params {object}
 * @param prependQuestionMark
 * @returns {string|string}
 */
export function paramsToUrlQuery(params, prependQuestionMark = true) {
  if (!params) return "";
  let urlQuery = [];
  if (params && Object.keys(params).length) {
    for (let key in params) {
      if (params.hasOwnProperty(key) && params[key] !== undefined && params[key] !== null)
        urlQuery.push(key + "=" + params[key]);
    }
  }
  return urlQuery.length ? (prependQuestionMark ? "?" : "&") + urlQuery.join("&") : "";
}

/**
 * Get tree of state data as variable paths
 * @param input
 * @param path
 * @param tree
 * @returns {*}
 */
export function getPathsTreeOfObject(input, path) {
  let tree = [];
  if (input) {
    for (let key in input) {
      const val = path ? path + "." + key : key;
      const elem = {
        id: val,
        label: val,
      };
      if (typeof input[key] === "object" && !Array.isArray(input[key])) {
        const children = getPathsTreeOfObject(input[key], val);
        if (children && children.length) {
          elem.children = children;
        }
      }
      tree.push(elem);
    }
  }
  return tree;
}

export function getBoundingBox(feature) {
  var bounds = {},
    coords,
    point,
    latitude,
    longitude;
  const depth = getArrayDepth(feature.geometry.coordinates);
  coords = feature.geometry.coordinates;
  if (["MultiPolygon", "Polygon"].includes(feature.geometry.type)) {
    if (depth === 3) coords = feature.geometry.coordinates[0];
    else if (depth === 4) {
      coords = [];
      feature.geometry.coordinates.forEach((c) => {
        coords.push(...c[0]);
      });
    }
  } else if (["MultiLineString", "LineString"].includes(feature.geometry.type) && depth === 3) {
    coords = [];
    feature.geometry.coordinates.forEach((c) => {
      coords.push(...c);
    });
  } else if (["MultiPoint", "Point"].includes(feature.geometry.type)) {
    if (depth === 2) {
      coords = [];
      feature.geometry.coordinates.forEach((c) => {
        coords.push(...c);
      });
    } else coords = [feature.geometry.coordinates];
  }
  for (var j = 0; j < coords.length; j++) {
    longitude = coords[j][0];
    latitude = coords[j][1];
    bounds.xMin = bounds.xMin < longitude ? bounds.xMin : longitude;
    bounds.xMax = bounds.xMax > longitude ? bounds.xMax : longitude;
    bounds.yMin = bounds.yMin < latitude ? bounds.yMin : latitude;
    bounds.yMax = bounds.yMax > latitude ? bounds.yMax : latitude;
  }

  return bounds;
}

export function normalizeString(string) {
  if (!string) return "";
  return String(string)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

export function getComputedValue(value, properties, returnEmptyOnError, dataVariable) {
  if (!value) return "";
  if (!dataVariable) dataVariable = "properties";

  let vars = {};
  vars[dataVariable] = properties;

  return String(value).interpolateObject(vars);
}

export function stripHtml(html) {
  if (!html) return "";
  let txt = new DOMParser().parseFromString(html, "text/html");
  return txt.documentElement.textContent.trim();
}

export function truncateString(str, length) {
  if (!str) return "";
  if (str.length <= length) {
    return str;
  }
  return str.slice(0, length) + "...";
}

export function findDocumentRecursively(list, documentId) {
  if (!Array.isArray(list)) return null;
  let result = null;
  if (list && list.length) result = list.find((l) => l.documentId === documentId);
  if (!result) {
    let children = list.filter((x) => x.children && x.children.length);
    if (children && children.length)
      result = findDocumentRecursively([...new Set([].concat(...children.map((x) => x.children)))], documentId);
  }
  return result;
}

export function recursiveFind(list, callback, childrenField = "children") {
  let result = list.find((item) => {
    return callback(item);
  });
  if (result) {
    return result;
  }

  for (let index = 0; index < list.length; index++) {
    if (!list[index].hasOwnProperty(childrenField || list[index][childrenField].length === 0)) {
      continue;
    }
    result = recursiveFind(list[index][childrenField], callback, childrenField);
    if (result) {
      return result;
    }
  }

  return undefined;
}

export function recursiveFindBefore(list, current) {
  let before = undefined;
  let parent = recursiveFindParent(list, (item) => item.documentId === current.documentId);
  if (parent) {
    let parentIndex = parent.children.findIndex((item) => item.documentId === current.documentId);
    if (parentIndex === 0) {
      return parent;
    }
    before = parent.children[parentIndex - 1];
    if (before.children?.length > 0) {
      before = recursiveFindLast(before.children);
    }

    return before;
  }

  let currentIndex = list.findIndex((item) => item.documentId === current.documentId);

  if (currentIndex === -1) {
    before = list[list.length - 1];
  } else if (currentIndex > 0) {
    before = list[currentIndex - 1];
  }

  if (before && before.children?.length > 0) {
    before = recursiveFindLast(before.children);
  }

  return before;
}

export function recursiveFindLast(list, childrenField = "children") {
  let last = list[list.length - 1];
  if (last.hasOwnProperty(childrenField) && last[childrenField].length > 0) {
    return recursiveFindLast(last[childrenField], childrenField);
  }
  return last;
}

export function recursiveFindParent(list, callback, parent = undefined, childrenField = "children") {
  let result = list.find((item) => {
    return callback(item);
  });
  if (result) {
    return parent;
  }

  for (let index = 0; index < list.length; index++) {
    if (!list[index][childrenField]?.length) {
      continue;
    }
    result = recursiveFindParent(list[index][childrenField], callback, list[index], childrenField);
    if (result) {
      return result;
    }
  }

  return undefined;
}

export function updateLayersRecursive(toUpdate, valuesFrom) {
  return toUpdate.map((itemToUpdate, index) => {
    let itemToSource = recursiveFind(valuesFrom, (item) => item.documentId === itemToUpdate.documentId);
    if (!itemToSource) {
      return itemToUpdate;
    }
    if (itemToUpdate.children?.length > 0) {
      return {...itemToSource, children: updateLayersRecursive(itemToUpdate.children, valuesFrom)};
    }

    return {...itemToSource, children: undefined};
  });
}

export function extractDataFromFeatures(features, project) {
  let featuresData = [];
  for (let x = 0; x < features.length; x++) {
    const layerGeometry = features[x];
    const feature = layerGeometry.geometry;
    if (!feature?.properties?.cluster) {
      // is not a cluster feature
      let item = {
        ...layerGeometry,
        properties: feature?.properties,
      };

      if (item.document?.properties?.header) {
        item.attributes = item.document.properties.header;
      }
      featuresData.push(item);
    }
  }
  return featuresData;
}

export function removeTreeItemByPropValue(tree, prop, value) {
  let result = [];
  for (let item of tree) {
    if (item.hasOwnProperty(prop) && item[prop] !== value) {
      if (item.children && item.children.length) {
        result.push({
          ...item,
          children: [...removeTreeItemByPropValue(item.children, prop, value)],
        });
      } else {
        result.push(item);
      }
    }
  }
  return result;
}

/**
 * Create a linear array from a nested array
 * @param data
 * @param result
 * @param childrenKey
 * @returns {*}
 */
export function flatTree(data, result, childrenKey = 'children') {
  for (let item of data) {
    let copyOfItem = JSON.parse(JSON.stringify(item));
    delete copyOfItem[childrenKey];

    result.push(copyOfItem);

    if (item[childrenKey]) {
      result = flatTree(item[childrenKey], result);
    }
  }
  return result;
}

/**
 * @returns {string}
 */
export function apiBaseUrl(version) {
  if (config.USE_VERSIONED_API)
    switch (version) {
      case 'v2':
        return config.API_URL_V2 ? config.API_URL_V2 : window.location.protocol + "//" + window.location.host + "/api/v2/";
        break;
      default:
        return config.APP_URL ? config.API_URL : window.location.protocol + "//" + window.location.host + "/api/v1/";
    }
  else return config.APP_URL ? config.API_URL : window.location.protocol + "//" + window.location.host + "/api/v1/";
}

/**
 * Handles auth injection into map related requests
 * @param url
 * @param resourceType
 * @returns {{headers: {"x-access-token"}, url}}
 */
export function mapRequestTransformer(url, resourceType, token, controller) {
  if (isOffline() && url.includes("/fonts/")) {
    url = decodeURIComponent(url);
    const parts = url.split("/");
    const fontIndex = parts.findIndex(part => part === "fonts") + 1;
    if (fontIndex > 0 && fontIndex < parts.length) {
      const fontstack = parts[fontIndex];
      if (fontstack.includes(",")) {
        parts[fontIndex] = fontstack.split(",")[0];
        url = parts.join("/");
      }
    }
  }

  if (
    url.includes(`://${window.location.host}`) /* same host */ ||
    (["Source", "Tile"].includes(resourceType) && config.SYNCMAP_URLS.find((x) => url.includes(x)))
  ) {
    return {
      url: url,
      headers: {"x-access-token": token},
    };
  }
  else {
    const u = new URL(url, window.location.href);
    const tag = u.searchParams.get("__authTag");
    if (tag && controller?.getLayerHeader(tag)) {

      return {url, headers: {...(controller?.getLayerHeader(tag) ?? {}), Origin: u.origin}}
    }

  }
}

/**
 * Trigger window resize event (force mapboxgl to adjust to various screen size changes)
 */
export function triggerResize() {
  setTimeout(() => window.dispatchEvent(new Event("resize")), 200);
}

export function extractImagesFromLayer(layerProperties, layerType) {
  let images, borderImages;

  switch (layerType) {
    case "circle":
      images = extractAttributeImages(layerProperties, "icon-image", "icon-color");
      break;
    case "fill":
      images = extractAttributeImages(layerProperties, "fill-pattern", "fill-color");
      borderImages = extractAttributeImages(layerProperties, "line-pattern", "line-color");
      if (images?.iconImages && borderImages?.iconImages) {
        images.iconImages = [...images.iconImages, ...borderImages.iconImages];
      }
      break;
    case "line":
      images = extractAttributeImages(layerProperties, "line-pattern", "line-color", 'line-dasharray');
      break;
  }

  return {images, borderImages};
}

export function extractAttributeImages(layerProperties, attribute, colorAttribute, dasharrayAttribute) {
  let iconImageType;
  if (
    layerProperties.styleExpression &&
    layerProperties?.styleRules?.length &&
    new RegExp(attribute, "gi").test(JSON.stringify(layerProperties.styleExpression))
  ) {
    iconImageType = "expression";
  } else if (layerProperties.style && layerProperties.style[attribute]) {
    iconImageType = "regular";
  }

  let iconImages = [];
  let iconImageValue = "";
  if (iconImageType === "regular") {
    iconImages.push({
      url: layerProperties.style[attribute],
      id: "syncmap_image_" + parseInt(String(Math.random() * 10000)),
      sdf: colorAttribute ? !!layerProperties.style[colorAttribute] : null,
      gap: dasharrayAttribute ? !!layerProperties.style[dasharrayAttribute] : 0,
    });
    iconImageValue = iconImages[0].id;
  }

  if (iconImageType === "expression") {
    const value = parseExpressionImages(
      layerProperties.styleExpression[attribute],
      [],
      colorAttribute ? !!layerProperties.style[colorAttribute] : null
    );
    iconImages = value.images;
    iconImageValue = value.expression;
    if (colorAttribute) {
      const valueColor = parseColorExpression(layerProperties.styleExpression[colorAttribute], []);
      if (valueColor && valueColor.values)
        valueColor.values.forEach((color, index) => {
          if (iconImages && iconImages.length > index) iconImages[index].color = color;
        });
    }
    if (dasharrayAttribute) {
      const valueDash = parseDashExpression(layerProperties.styleExpression[dasharrayAttribute], []);
      if (valueDash && valueDash.values)
        valueDash.values.forEach((dash, index) => {
          if (iconImages && iconImages.length > index) iconImages[index].gap = (dash.length > 1 ? dash[1] : 0);
        });
    }
  }
  return {iconImages, iconImageValue, iconImageType};
}

export function extractLabelImages(layerProperties, attribute, colorAttribute) {
  let iconImageType;
  if (
    layerProperties.labelExpression &&
    layerProperties?.labelRules?.length &&
    new RegExp(attribute, "gi").test(JSON.stringify(layerProperties.labelExpression))
  ) {
    iconImageType = "expression";
  } else if (layerProperties.label && layerProperties.label[attribute]) {
    iconImageType = "regular";
  }

  let iconImages = [];
  let iconImageValue = "";

  if (iconImageType === "regular") {
    iconImages.push({
      url: layerProperties.label[attribute],
      id: "syncmap_image_" + parseInt(String(Math.random() * 10000)),
      sdf: colorAttribute ? !!layerProperties.label[colorAttribute] : null
    });
    iconImageValue = iconImages[0].id;
  }

  if (iconImageType === "expression") {
    const value = parseExpressionImages(layerProperties.labelExpression[attribute], [], false);
    iconImages = value.images;
    iconImageValue = value.expression;
    if (colorAttribute) {
      const valueColor = parseColorExpression(layerProperties.labelExpression[colorAttribute], []);
      if (valueColor && valueColor.values)
        valueColor.values.forEach((color, index) => {
          if (iconImages && iconImages.length > index) iconImages[index].color = color;
        });
    }
  }

  return {iconImages, iconImageValue, iconImageType};
}

export function createHatch(meta, mapInstance) {
  const size = Math.max(4, Math.ceil(meta.spacing));
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");

  ctx.clearRect(0, 0, size, size);

  ctx.strokeStyle = meta.color;
  ctx.globalAlpha = meta.opacity;
  ctx.lineWidth = meta.width;

  const radians = (meta.angle * Math.PI) / 180;
  ctx.translate(size / 2, size / 2);
  ctx.rotate(radians);
  ctx.translate(-size / 2, -size / 2);

  ctx.beginPath();
  ctx.moveTo(-size, size / 2);
  ctx.lineTo(size * 2, size / 2);
  ctx.stroke();

  return {
    width: size,
    height: size,
    data: ctx.getImageData(0, 0, size, size).data
  };
}

/**
 *
 * @param images
 * @param {mapboxgl.Map} mapInstance
 */
export function loadMapImages(images, mapInstance) {
  Promise.all(
    images.map(
      (currentImage) =>
        new Promise((resolve, reject) => {
          //console.log(img.url);
          if (!currentImage.url) {

            if (currentImage.meta) {
              if (!mapInstance.hasImage(currentImage.id)) {
                let imageData = createHatch(currentImage.meta, mapInstance)
                mapInstance.addImage(currentImage.id, imageData, {pixelRatio: 1});
              }
            }
            else {
              const rgb = convertToRGB(
                currentImage.color ? currentImage.color.replace("#", "") : currentImage.color
              );
              if (rgb) {
                const width = 64;
                const bytesPerPixel = 4;
                const data = new Uint8Array(width * width * bytesPerPixel);

                for (let x = (currentImage.gap || 0) * bytesPerPixel; x < width; x++) {
                  for (let y = (currentImage.gap || 0) * bytesPerPixel; y < width; y++) {
                    const offset = (y * width + x) * bytesPerPixel;
                    data[offset] = rgb[0];
                    data[offset + 1] = rgb[1];
                    data[offset + 2] = rgb[2];
                    data[offset + 3] = 255;
                  }
                }
                if (!mapInstance.hasImage(currentImage.id)) {
                  try {
                    mapInstance.addImage(currentImage.id, {
                      width: 64,
                      height: 64,
                      data: data,
                    });
                  } catch (error) {
                    reject(error);
                  }
                }
              }
            }
            resolve();
          } else {
            if (
              config.APP_TRANSFORM_STORAGE_URL &&
              currentImage?.url &&
              !new URL(currentImage.url).host.includes(window.location.host)
            ) {
              currentImage.url = currentImage.url.replace(
                new URL(currentImage.url).host,
                window.location.host
              );
            }
            mapInstance.loadImage(currentImage.url, (error, res) => {
              if (error) reject(error);
              if (!mapInstance.hasImage(currentImage.id)) {
                try {
                  mapInstance.addImage(currentImage.id, res, {sdf: currentImage.sdf});
                } catch (error) {
                  reject(error);
                }
                resolve();
              }
            });
          }
        })
    )
  );
}

/**
 * Filter mapbox style object for properties that accept expressions
 * @param style
 * @returns {*}
 */
export function getExpressionOnlyStyle(style) {
  if (!style) return {};

  const mapboxExpressionProps = getAllMapboxExpressionStyleProperties();
  for (let prop in style) {
    if (!mapboxExpressionProps.hasOwnProperty(prop)) {
      delete style[prop];
    }
  }
  return style;
}

/**
 * Get object of all mapbox properties that accept expressions
 * @returns {{}}
 */
export function getAllMapboxExpressionStyleProperties() {
  let expressionProps = {};
  for (let list of Object.values(mapboxStyles)) {
    expressionProps = {
      ...expressionProps,
      ...list,
    };
  }
  for (let prop in expressionProps) {
    if (!expressionProps[prop].expressions) {
      delete expressionProps[prop];
    }
  }
  return expressionProps;
}

/**
 * Get object of all mapbox properties that accept featureState
 * @returns {{}}
 */
export function getAllMapboxFeatureStateStyleProperties() {
  let expressionProps = {};
  for (let list of Object.values(mapboxStyles)) {
    expressionProps = {
      ...expressionProps,
      ...list,
    };
  }
  for (let prop in expressionProps) {
    if (!expressionProps[prop].featureState) {
      delete expressionProps[prop];
    }
  }
  return expressionProps;
}

/**
 * Add layer into projects nested array based on parentDocumentId
 * @param projects
 * @param layer
 * @returns {*}
 */
export function addLayerToProjectsList(projects, layer) {
  for (let i = 0; i < projects.length; i++) {
    if (projects[i].documentId === layer.parentDocumentId) {
      if (!projects[i].children) projects[i].children = [];
      projects[i].children.push(layer);
    } else if (projects[i]?.children?.length) {
      projects[i].children = addLayerToProjectsList(projects[i]?.children, layer);
    }
  }
  return projects;
}

/**
 * Remove layer from projects nested array
 * @param projects
 * @param layer
 * @returns {*}
 */
export function removeLayerFromProjects(projects, layer) {
  for (let i = 0; i < projects.length; i++) {
    if (projects[i].documentId === layer.documentId) {
      projects.splice(i, 1);
      break;
    } else if (projects[i]?.children?.length) {
      projects[i].children = removeLayerFromProjects(projects[i]?.children, layer);
    }
  }
  return projects;
}

/**
 * Update layer on projects nested array
 * @param projects
 * @param layer
 * @returns {*}
 */
export function updateLayerOnProjects(projects, layer) {
  for (let i = 0; i < projects.length; i++) {
    if (projects[i].documentId === layer.documentId) {
      projects[i] = {
        ...layer,
        children: projects[i].children,
      };
      break;
    } else if (projects[i]?.children?.length) {
      projects[i].children = updateLayerOnProjects(projects[i]?.children, layer);
    }
  }
  return projects;
}

export function orderNestedByPosition(arr) {
  if (!Array.isArray(arr)) return arr;
  arr.forEach(item => orderNestedByPosition(item))
  arr = arr.sort((a, b) => {
    if (!a.hasOwnProperty('position') || !b.hasOwnProperty('position')) return 0;
    return a.position - b.position;
  });
  return arr;
}

export function getElementsByPropertyValue(object, property, value) {
  let results = [];

  function search(obj) {
    if (obj && typeof obj === "object") {
      if (obj[property] === value) {
        results.push(obj);
      }

      for (let key in obj) {
        if (obj.hasOwnProperty(key)) {
          search(obj[key]);
        }
      }
    }
  }

  search(object);
  return results;
}

export function getSurfaceElementsByPropertyValue(object, property, value) {
  let results = [];

  function search(obj) {
    if (obj && typeof obj === "object") {
      if (obj[property] === value) {
        results.push(obj);
      } else {
        for (let key in obj) {
          if (obj.hasOwnProperty(key)) {
            search(obj[key]);
          }
        }
      }
    }
  }

  search(object);
  return results;
}

/**
 * @param ids
 * @param organizations
 * @param returnType
 * @param labelField
 * @param idField
 * @returns {string|*[]}
 */
export function getOrganizationNamesByIds(ids = [], organizations = [], returnType = 'string', labelField = 'title', idField = 'organizationId') {
  let organizationNames = [];

  for (let org of organizations) {
    if (ids?.indexOf(org[idField]) > -1) {
      organizationNames.push(org[labelField]);
    }
  }

  return returnType === 'string' ? organizationNames.join(", ") : organizationNames;
}

/**
 * Get a human friendly value of a styling rule
 * @param rule
 * @param translator
 * @returns {string}
 */
export function styleRuleToText(rule = {}, translator) {
  function getOperandValue(op) {
    switch (op.code) {
      case 'attribute':
        return op?.value?.title
      case 'zoom':
        return op.value
      case 'value':
        return op.value
    }
  }

  function getOperatorValue(op) {
    const operators = [
      {
        title: translator("does not exists"),
        code: "!",
      },
      {
        title: translator("is not equal with"),
        code: "!=",
      },
      {
        title: translator("is equal with"),
        code: "==",
      },
      /*{
        title: translator("match"),
        code: "match",
      },*/
      {
        title: translator("contains"),
        code: "index-of",
      },
      {
        title: translator("is greater than"),
        code: ">",
      },
      {
        title: translator("is greater or equal than"),
        code: ">=",
      },
      {
        title: translator("is lower than"),
        code: "<",
      },
      {
        title: translator("is lower or equal than"),
        code: "<=",
      },
    ]
    return operators.find(el => el.code === op).title
  }

  return [getOperandValue(rule.operand1), '<em>' + getOperatorValue(rule.operator) + '</em>', getOperandValue(rule.operand2)].join(" ");
}

export function callApi(method, url, headers, body, qs, retunResponse, responseType) {
  return new Promise((resolve, reject) => {
    let call = {
      headers,
      url: url,
      method: method,
      data: body,
      params: qs
    };
    if (responseType) call.responseType = responseType

    //axios(call)
    axios.get(url).then(resp => {
      if (retunResponse) {
        resolve(resp)
      } else {
        resolve(resp.data)
      }
    }).catch(err => {
      reject(err.response)
    })
  })
}

export function getTemplateVariable(name) {
  return /\${(.*)}/gm.test(name) ? String(name).replace(/\${(.*)}/gm, '$1') : name
}

export function applySchemaToData(data, schema) {
  let result, variable;
  if (Array.isArray(schema)) {
    if (Array.isArray(data))
      data.forEach(item => {
        let schemaItem = applySchemaToData(item, schema[0]);
        if (schemaItem) {
          if (!result) result = []
          result.push(schemaItem)
        }

      })
    else if (data) {
      if (typeof schema[0][Object.keys(schema[0])[0]] === "string") {
        variable = getTemplateVariable(schema[0][Object.keys(schema[0])[0]]);
        variable = '${' + variable.split('.$.')[0] + '}'
        result = applySchemaToData(variable.interpolateObject(data), schema)
      }
    } else {
      if (Array.isArray(schema))
        result = [];
    }

  } else if (typeof schema === "object") {
    let schemaItem = JSON.parse(JSON.stringify(schema))
    Object.keys(schema).forEach(key => {
      if (Array.isArray(schema[key])) {
        if (typeof schema[key] === "object") {
          schemaItem[key] = applySchemaToData(data, schema[key])
        } else if (typeof schema[key] === "string") {
          variable = getTemplateVariable(schema[key][0]);
          variable = '${' + variable.split('.$.')[0] + '}'
          schemaItem[key] = applySchemaToData(`${variable}`.interpolateObject(data), schema[key])
        }
      } else if (typeof schema[key] === "object")
        schemaItem[key] = applySchemaToData(data, schema[key])
      else if (typeof schema[key] === "string") {
        variable = getTemplateVariable(schema[key]);
        variable = '${' + variable.split('.$.').pop() + '}'
        schemaItem[key] = variable.interpolateObject(data);
      }
    })
    result = schemaItem;
  } else if (typeof schema === "string") {
    result = schema.interpolateObject(data);
  }
  return result;

}

// const StatusEnum = {
//   Active: 1,
//   Pending: 2,
//   Committed: 3,
//   Cancelled: 4,
//   Processing: 5,
//   Failed: 6,
//   Preparing: 7,
//   Converting: 8,
//   Prepared: 9,
//   Converted: 10,
//   Translated: 11,
//   Queued: 12,
//   Expired: 29,
// };
export const ALLOWED_STATUSES = [1, 2, 6, 3, 5];


// ADMIN: 2
// SUPER ADMIN: 1
export const ADMIN_ROLE_IDS = [1, 2];

export const copyItems = (elements, code) => {
  try {
    window.navigator.clipboard.writeText(JSON.stringify({code: code, data: elements}));
  } catch (err) {
    console.log(err);
  }
};

export const pasteItems = (toast, t, data, elementsKey, code) => {
  try {
    window.navigator.clipboard.readText().then(txt => {
      try {
        let newEl = JSON.parse(txt);
        if (newEl?.code !== code || (!newEl?.data || !Array.isArray(newEl?.data))) {
          toast.add({
            severity: "error",
            summary: "Error",
            detail: t("Wrong format!"),
            life: 5000,
          });
          return;
        }
        if (!data[elementsKey]) {
          data[elementsKey] = []
        }
        data[elementsKey].push(...newEl.data);
      } catch (err) {
        console.log(err);
        toast.add({
          severity: "error",
          summary: "Error",
          detail: t("Wrong format!"),
          life: 5000,
        });
      }
    });
  } catch (err) {
    console.log(err);
    toast.add({
      severity: "error",
      summary: "Error",
      detail: t("Wrong format!"),
      life: 5000,
    });
  }
}

export const getFilteredObjectByKeyPrefix = (object, prefix, property = null) => {
  if (property) {
    object = filterMapboxPropsByType(object, prefix, property);
  }
  let filtered = {};
  for (let key in object) {
    if (key.startsWith(prefix) && object[key] !== "") {
      filtered[key] = object[key];
    }
  }
  return filtered
}

export const schemaToFlatKeys = (schema) => {
  const keys = [];
  const parseFieldList = (fl, prefix) => {
    Object.keys(fl)?.forEach(key => {
      const currentKey = `${prefix ? prefix + '.' : ''}${key}`
      keys.push(currentKey);
      if (typeof fl[key] === 'object' && fl[key]?.fieldList) {
        parseFieldList(fl[key].fieldList, currentKey)
      }
    })
  };
  parseFieldList(schema.fieldList);
  return keys;
}

export const interpolateFields = (obj, params, mappings) => {
  if (obj) {
    if (Array.isArray(obj)) {
      obj.forEach(item => {
        interpolateFields(item, params, mappings);
      })
    } else if (typeof obj === "string") {
      obj = obj.interpolateObject({...params, ...mappings})
    } else
      Object.keys(obj).forEach(key => {
        if (Array.isArray(obj[key])) {
          obj[key].forEach((item, index) => {
            if (typeof item === "string")
              obj[key][index] = item.interpolateObject({...params, ...mappings})
            else
              interpolateFields(item, params, mappings)
          })
        } else if (typeof obj[key] === "object")
          interpolateFields(obj[key], params, mappings)
        else if (typeof obj[key] === "string") obj[key] = obj[key].interpolateObject({...params, ...mappings})
      })
  }
}

export const mergePdfs = (pdfBuffers) => {
  return new Promise(async (resolve, reject) => {
    try {
      const {PDFDocument} = await import('pdf-lib');
      const mergedPdf = await PDFDocument.create();

      for (const pdfBuffer of pdfBuffers) {

        const pdfDoc = await PDFDocument.load(pdfBuffer);
        const copiedPages = await mergedPdf.copyPages(pdfDoc, pdfDoc.getPageIndices());
        copiedPages.forEach((page) => mergedPdf.addPage(page));
      }

      const mergedPdfBytes = await mergedPdf.save();
      resolve(mergedPdfBytes)
    } catch (error) {
      reject(error);
    }
  });
}

export const streamToArrayBuffer = (stream) => {
  return new Promise(async (resolve, reject) => {
    try {
      const reader = stream.getReader();
      const chunks = [];

      while (true) {
        const {done, value} = await reader.read();
        if (done) break;
        chunks.push(value);
      }

      const totalLength = chunks.reduce((acc, chunk) => acc + chunk.length, 0);
      const result = new Uint8Array(totalLength);
      let position = 0;
      for (const chunk of chunks) {
        result.set(chunk, position);
        position += chunk.length;
      }

      resolve(result.buffer);
    } catch (error) {
      reject(error);
    }
  });
}


export function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onloadend = () => {
      const base64data = reader.result; // This contains the Base64 string
      resolve(base64data);
    };

    reader.onerror = () => {
      reject(new Error('Failed to convert Blob to Base64'));
    };

    reader.readAsDataURL(blob); // Read the Blob as a data URL
  });
}

export function isUUID(uuid) {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuidRegex.test(uuid);
}

export function saveTemplateAsFile(filename, dataObjToWrite) {
  const blob = new Blob([JSON.stringify(dataObjToWrite)], { type: 'text/json' });
  const link = document.createElement('a');

  link.download = filename;
  link.href = window.URL.createObjectURL(blob);
  link.dataset.downloadurl = ['text/json', link.download, link.href].join(':');

  const evt = new MouseEvent('click', {
    view: window,
    bubbles: true,
    cancelable: true,
  });

  link.dispatchEvent(evt);
  link.remove();
}

export {isOffline};
